import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
  IconPlus,
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconDownload,
} from './Icons';
import { getDefaultStampAndSignature } from '../lib/companyAssets';
import {
  sendProcurementSupplierEmailsFromClient,
  sendPurchaseOrderEmailToSupplier,
  sendProcurementRequestCreatedEmail,
  sendPurchaseOrderCreatedEmail,
} from '../services/procurementEmailService';
import headImg from '../head.png';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Tenders.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const procurementSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

// Procurement tab does multiple Supabase queries in parallel; keep this
// tolerant enough for slower networks while still failing fast.
const FETCH_TIMEOUT_MS = 30000;

const headImgUrl = headImg;

const COMPANY_PHONE = '+968 9888 3137';
const COMPANY_ADDRESS = 'P.O.Box 121, Building No: 7351, Flat No: 11 & 12, Mabelah Muscat, Sultanate of Oman';
const COMPANY_EMAIL = 'ecdtinfo@ecdtooman.com';
const COMPANY_WEB = 'www.ecdtoman.com';

// Shared PDF layout: same headers, footers and margins for Procurement Request and Purchase Order
const PDF_MARGIN_MM = 10;
const PDF_HEADER_HEIGHT_MM = 28;
const PDF_FOOTER_RESERVE_MM = 24; // space at bottom so tables don't overlap footer
const PDF_FOOTER_TEXT_Y_OFFSET_MM = 18; // contact line from bottom
const PDF_FOOTER_BAR_HEIGHT_MM = 6; // orange bar height

/** VAT on PO line subtotal only (shipping excluded). Fixed 5%; not user-editable. */
const PO_TAX_PERCENT_FIXED = 5;

function computePoTaxAndGrandTotal(subtotal, shippingCost) {
  const sub = Math.max(0, Number(subtotal) || 0);
  const ship = Math.max(0, Number(shippingCost) || 0);
  const taxAmount = Math.round(sub * (PO_TAX_PERCENT_FIXED / 100) * 100) / 100;
  const grandTotal = Math.round((sub + ship + taxAmount) * 100) / 100;
  return { taxAmount, grandTotal };
}

/** Draw header image and footer (contact line + orange bar) on current page. Use in didDrawPage for both PR and PO. */
function drawPdfHeaderFooter(pdf, pageW, pageH, headerDataUrl) {
  if (headerDataUrl) {
    pdf.addImage(headerDataUrl, 'PNG', 0, 0, pageW, PDF_HEADER_HEIGHT_MM);
  }
  const footerY = pageH - PDF_FOOTER_TEXT_Y_OFFSET_MM;
  pdf.setFontSize(8);
  pdf.setTextColor(15, 23, 42);
  pdf.text(`${COMPANY_PHONE}  |  ${COMPANY_ADDRESS}  |  ${COMPANY_EMAIL}  |  ${COMPANY_WEB}`, pageW / 2, footerY, { align: 'center' });
  pdf.setFillColor(230, 126, 34);
  pdf.rect(0, pageH - PDF_FOOTER_BAR_HEIGHT_MM, pageW, PDF_FOOTER_BAR_HEIGHT_MM, 'F');
}

/** Load image from URL (or data URL) and return as data URL for use in jsPDF. */
async function loadImageDataUrl(src) {
  if (!src) return null;
  try {
    const resp = await fetch(src);
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ---------- Mock data ----------

const MOCK_SUPPLIERS = [
  {
    id: 's1',
    companyName: 'Al Noor Trading LLC',
    contactPerson: 'Mohammed Al-Noor',
    phone: '+968 2412 3400',
    email: 'sales@alnoor.om',
    category: 'Electrical',
    notes: 'Preferred for lighting and small power materials.',
    address: 'Muscat, Oman',
  },
  {
    id: 's2',
    companyName: 'Gulf Concrete Products',
    contactPerson: 'Khalid Al-Harthy',
    phone: '+968 2450 1122',
    email: 'info@gcp.om',
    category: 'Concrete / Civil',
    notes: 'Ready-mix concrete supplier for Muscat area.',
    address: 'Ghala Industrial Area, Muscat',
  },
  {
    id: 's3',
    companyName: 'Desert Steel Fabrication',
    contactPerson: 'Fatima Al-Balushi',
    phone: '+968 2466 7788',
    email: 'orders@desertsteel.om',
    category: 'Steel',
    notes: 'Fabricated steel structures and handrails.',
    address: 'Sohar, Oman',
  },
  {
    id: 's4',
    companyName: 'Modern Electromechanical Co.',
    contactPerson: 'Ahmed Al-Rawahi',
    phone: '+968 2443 5566',
    email: 'tenders@memc.om',
    category: 'MEP',
    notes: 'MEP packages and specialist vendors.',
    address: 'Ruwi, Muscat',
  },
  {
    id: 's5',
    companyName: 'Ibri Building Materials',
    contactPerson: 'Laila Al-Habsi',
    phone: '+968 2578 9900',
    email: 'orders@ibri-bm.om',
    category: 'General materials',
    notes: 'Regional supplier for Ibri projects.',
    address: 'Ibri, Oman',
  },
  {
    id: 's6',
    companyName: 'Oman Fasteners & Tools',
    contactPerson: 'James Wilson',
    phone: '+968 2456 7789',
    email: 'sales@oft.om',
    category: 'Tools',
    notes: 'Hand tools, fixings and consumables.',
    address: 'Wadi Kabir, Muscat',
  },
  {
    id: 's7',
    companyName: 'Blue Water Pumps',
    contactPerson: 'Hassan Al-Muqbali',
    phone: '+968 2688 3344',
    email: 'support@bluewaterpumps.om',
    category: 'Pumps',
    notes: 'Dewatering and water transfer pumps.',
    address: 'Barka, Oman',
  },
  {
    id: 's8',
    companyName: 'Elite Safety Supplies',
    contactPerson: 'Sara Al-Lawati',
    phone: '+968 2422 9900',
    email: 'orders@elitesafety.om',
    category: 'PPE / Safety',
    notes: 'PPE, safety shoes, helmets, signage.',
    address: 'Muscat, Oman',
  },
];

const MOCK_REQUESTS = [
  {
    id: 'pr1',
    requestId: 'PR-2026-001',
    title: 'Concrete for Muscat Airport apron',
    departmentProject: 'Civil / Muscat Airport Expansion',
    requestedBy: 'Ahmed Al-Rashdi',
    neededDate: '2026-03-20',
    suppliersInvited: 3,
    selectedSupplier: 'Gulf Concrete Products',
    status: 'Converted to Order',
  },
  {
    id: 'pr2',
    requestId: 'PR-2026-002',
    title: 'Site office furniture – Salalah',
    departmentProject: 'Admin / Salalah Water Treatment Plant',
    requestedBy: 'Sara Al-Lawati',
    neededDate: '2026-04-05',
    suppliersInvited: 4,
    selectedSupplier: 'Ibri Building Materials',
    status: 'Supplier Selected',
  },
  {
    id: 'pr3',
    requestId: 'PR-2026-003',
    title: 'Safety PPE for Ibri Solar site',
    departmentProject: 'HSE / Ibri Solar O&M',
    requestedBy: 'Fatima Al-Balushi',
    neededDate: '2026-03-25',
    suppliersInvited: 2,
    selectedSupplier: 'Elite Safety Supplies',
    status: 'Offers Received',
  },
  {
    id: 'pr4',
    requestId: 'PR-2026-004',
    title: 'Submersible pumps for dewatering',
    departmentProject: 'MEP / Barka Desalination Pipeline',
    requestedBy: 'Khalid Al-Harthi',
    neededDate: '2026-03-30',
    suppliersInvited: 3,
    selectedSupplier: '',
    status: 'RFQ Sent',
  },
  {
    id: 'pr5',
    requestId: 'PR-2026-005',
    title: 'Hand tools for maintenance team',
    departmentProject: 'Maintenance / Workshop',
    requestedBy: 'Maintenance Supervisor',
    neededDate: '2026-03-18',
    suppliersInvited: 1,
    selectedSupplier: 'Oman Fasteners & Tools',
    status: 'Closed',
  },
  {
    id: 'pr6',
    requestId: 'PR-2026-006',
    title: 'LV cables for Muttrah lighting',
    departmentProject: 'Electrical / Muttrah Corniche Lighting',
    requestedBy: 'Electrical Engineer',
    neededDate: '2026-04-10',
    suppliersInvited: 2,
    selectedSupplier: '',
    status: 'Draft',
  },
  {
    id: 'pr7',
    requestId: 'PR-2026-007',
    title: 'GI ducts for Nizwa Hospital',
    departmentProject: 'MEP / Nizwa Hospital HVAC',
    requestedBy: 'MEP Coordinator',
    neededDate: '2026-03-28',
    suppliersInvited: 3,
    selectedSupplier: '',
    status: 'Offers Received',
  },
  {
    id: 'pr8',
    requestId: 'PR-2026-008',
    title: 'IT network racks – ROP HQ',
    departmentProject: 'IT / ROP HQ',
    requestedBy: 'IT Manager',
    neededDate: '2026-04-01',
    suppliersInvited: 2,
    selectedSupplier: 'Modern Electromechanical Co.',
    status: 'Supplier Selected',
  },
  {
    id: 'pr9',
    requestId: 'PR-2026-009',
    title: 'Temporary fencing – Sur Industrial Estate',
    departmentProject: 'Civil / Sur Industrial Roads',
    requestedBy: 'Site Engineer',
    neededDate: '2026-03-22',
    suppliersInvited: 2,
    selectedSupplier: '',
    status: 'RFQ Sent',
  },
  {
    id: 'pr10',
    requestId: 'PR-2026-010',
    title: 'Office IT equipment – Head Office',
    departmentProject: 'IT / Head Office',
    requestedBy: 'HR Manager',
    neededDate: '2026-04-15',
    suppliersInvited: 3,
    selectedSupplier: '',
    status: 'Draft',
  },
];

const MOCK_ORDERS = [
  {
    id: 'po1',
    orderId: 'PO-2026-001',
    supplier: 'Gulf Concrete Products',
    linkedRequestId: 'PR-2026-001',
    linkedRequestTitle: 'Concrete for Muscat Airport apron',
    totalAmount: 185000,
    orderDate: '2026-03-05',
    deliveryStatus: 'Delivered',
    paymentStatus: 'Paid',
    supplierContact: 'Khalid Al-Harthy',
    supplierPhone: '+968 2450 1122',
    supplierEmail: 'info@gcp.om',
    items: [
      { description: 'C40 concrete – apron slab', quantity: 1200, unit: 'm³', amount: 165000 },
      { description: 'Concrete pump & placing boom', quantity: 1, unit: 'LS', amount: 20000 },
    ],
    invoice: { fileName: 'INV-GCP-2026-045.pdf', amount: 185000, date: '2026-03-12' },
  },
  {
    id: 'po2',
    orderId: 'PO-2026-002',
    supplier: 'Elite Safety Supplies',
    linkedRequestId: 'PR-2026-003',
    linkedRequestTitle: 'Safety PPE for Ibri Solar site',
    totalAmount: 14500,
    orderDate: '2026-03-02',
    deliveryStatus: 'Partial',
    paymentStatus: 'Partially Paid',
    supplierContact: 'Sara Al-Lawati',
    supplierPhone: '+968 2422 9900',
    supplierEmail: 'orders@elitesafety.om',
    items: [
      { description: 'Safety helmets', quantity: 80, unit: 'pc', amount: 3200 },
      { description: 'High visibility vests', quantity: 80, unit: 'pc', amount: 2800 },
      { description: 'Safety shoes', quantity: 80, unit: 'pair', amount: 8500 },
    ],
    invoice: { fileName: 'INV-ESS-2026-010.pdf', amount: 10000, date: '2026-03-08' },
  },
  {
    id: 'po3',
    orderId: 'PO-2026-003',
    supplier: 'Desert Steel Fabrication',
    linkedRequestId: 'PR-2026-007',
    linkedRequestTitle: 'GI ducts for Nizwa Hospital',
    totalAmount: 68000,
    orderDate: '2026-03-10',
    deliveryStatus: 'Pending',
    paymentStatus: 'Unpaid',
    supplierContact: 'Fatima Al-Balushi',
    supplierPhone: '+968 2466 7788',
    supplierEmail: 'orders@desertsteel.om',
    items: [
      { description: 'Fabricated GI ducts', quantity: 1, unit: 'LS', amount: 65000 },
      { description: 'Accessories and supports', quantity: 1, unit: 'LS', amount: 3000 },
    ],
    invoice: null,
  },
  {
    id: 'po4',
    orderId: 'PO-2026-004',
    supplier: 'Oman Fasteners & Tools',
    linkedRequestId: 'PR-2026-005',
    linkedRequestTitle: 'Hand tools for maintenance team',
    totalAmount: 5200,
    orderDate: '2026-02-20',
    deliveryStatus: 'Delivered',
    paymentStatus: 'Paid',
    supplierContact: 'James Wilson',
    supplierPhone: '+968 2456 7789',
    supplierEmail: 'sales@oft.om',
    items: [
      { description: 'Hand tools set', quantity: 10, unit: 'set', amount: 5200 },
    ],
    invoice: { fileName: 'INV-OFT-2026-021.pdf', amount: 5200, date: '2026-02-25' },
  },
  {
    id: 'po5',
    orderId: 'PO-2026-005',
    supplier: 'Blue Water Pumps',
    linkedRequestId: 'PR-2026-004',
    linkedRequestTitle: 'Submersible pumps for dewatering',
    totalAmount: 36000,
    orderDate: '2026-03-08',
    deliveryStatus: 'Partial',
    paymentStatus: 'Unpaid',
    supplierContact: 'Hassan Al-Muqbali',
    supplierPhone: '+968 2688 3344',
    supplierEmail: 'support@bluewaterpumps.om',
    items: [
      { description: '6\" submersible pump sets', quantity: 4, unit: 'set', amount: 32000 },
      { description: 'Spare parts kit', quantity: 1, unit: 'LS', amount: 4000 },
    ],
    invoice: null,
  },
  {
    id: 'po6',
    orderId: 'PO-2026-006',
    supplier: 'Al Noor Trading LLC',
    linkedRequestId: 'PR-2026-006',
    linkedRequestTitle: 'LV cables for Muttrah lighting',
    totalAmount: 24500,
    orderDate: '2026-03-12',
    deliveryStatus: 'Pending',
    paymentStatus: 'Unpaid',
    supplierContact: 'Mohammed Al-Noor',
    supplierPhone: '+968 2412 3400',
    supplierEmail: 'sales@alnoor.om',
    items: [
      { description: 'LV cables 4C x 16mm²', quantity: 12, unit: 'drum', amount: 24500 },
    ],
    invoice: null,
  },
];

// ---------- Helpers ----------

function getRequestStatusBadge(status) {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'tenders-badge-draft';
  if (s === 'rfq sent') return 'tenders-badge-submitted';
  if (s === 'offers received') return 'tenders-badge-under-review';
  if (s === 'supplier selected') return 'tenders-badge-approved';
  if (s === 'converted to order') return 'tenders-badge-awarded';
  if (s === 'closed') return 'tenders-badge-cancelled';
  return 'tenders-badge-draft';
}

function getDeliveryBadge(status) {
  const s = (status || '').toLowerCase();
  if (s === 'pending') return 'tenders-badge-pending';
  if (s === 'partial') return 'tenders-badge-under-review';
  if (s === 'delivered') return 'tenders-badge-done';
  return 'tenders-badge-draft';
}

function getPaymentBadge(status) {
  const s = (status || '').toLowerCase();
  if (s === 'unpaid') return 'tenders-badge-not-approved';
  if (s === 'partially paid') return 'tenders-badge-under-review';
  if (s === 'paid') return 'tenders-badge-approved';
  return 'tenders-badge-draft';
}

function getRfqStatusBadge(status) {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'tenders-badge-draft';
  if (s === 'sent') return 'tenders-badge-pending';
  if (s === 'accepted') return 'tenders-badge-approved';
  if (s === 'rejected' || s === 'expired') return 'tenders-badge-cancelled';
  return 'tenders-badge-draft';
}

function rfqLineTotal(qty, unitPrice) {
  const q = Math.max(0, Number(qty) || 0);
  const p = Math.max(0, Number(unitPrice) || 0);
  return Math.round(q * p * 100) / 100;
}

function rfqSubtotal(items) {
  return (items || []).reduce((sum, row) => sum + rfqLineTotal(row.qty, row.unitPrice), 0);
}

function rfqTotalsWithVat(items) {
  const sub = rfqSubtotal(items);
  return computePoTaxAndGrandTotal(sub, 0);
}

function mapRfqRecordFromDb(row) {
  if (!row) return null;
  const items = (row.rfq_line_items ?? [])
    .slice()
    .sort((a, b) => (Number(a.line_no) || 0) - (Number(b.line_no) || 0))
    .map((line, idx) => ({
      id: line.id || `line-${idx}`,
      item: line.item_name ?? '',
      description: line.description ?? '',
      qty: line.quantity != null ? Number(line.quantity) : '',
      unitPrice: line.unit_price != null ? Number(line.unit_price) : '',
    }));
  return {
    id: row.id,
    refNo: row.ref_no ?? '',
    documentDate: row.document_date ?? '',
    client: row.client ?? '',
    clientCountry: row.client_country ?? '',
    subject: row.subject ?? '',
    tenderNo: row.tender_no ?? '',
    warranty: row.warranty ?? '',
    delivery: row.delivery ?? '',
    status: row.status ?? 'Draft',
    grandTotal: row.grand_total != null ? Number(row.grand_total) : null,
    items,
  };
}

const createEmptyRfqForm = () => ({
  refNo: '',
  documentDate: new Date().toISOString().slice(0, 10),
  client: '',
  clientCountry: '',
  subject: '',
  tenderNo: '',
  warranty: '3 Years',
  delivery: '3 - 4 Weeks',
  status: 'Draft',
  items: [{ id: Date.now(), item: '', description: '', qty: '', unitPrice: '' }],
});

const PROCUREMENT_STAGES = [
  'Request Created',
  'RFQ Sent',
  'Supplier Selected',
  'Order Created',
  'Delivered',
  'Paid',
];

/** PR PDFs show department only; project title is omitted (combined label uses "Dept / Project"). */
function departmentLabelForPrPdf(combinedLabel) {
  const s = (combinedLabel ?? '').toString().trim();
  if (!s) return '';
  const sep = ' / ';
  const i = s.indexOf(sep);
  if (i === -1) return s;
  return s.slice(0, i).trim();
}

const emptySupplierForm = () => ({
  company_name: '',
  industry: '',
  category_products: '',
  location: '',
  primary_email: '',
  primary_phone: '',
  website: '',
  notes: '',
  bank_details: '',
  contacts: [
    {
      id: `contact-${Date.now()}`,
      name: '',
      role: '',
      email: '',
      phone: '',
      whatsapp: '',
      is_primary: true,
    },
  ],
});

const getPrimaryContact = (supplier) => {
  const contacts = supplier?.contacts;
  if (!Array.isArray(contacts) || contacts.length === 0) return null;
  return contacts.find((c) => c.is_primary) || contacts[0] || null;
};

function numberToWordsUnderThousand(n) {
  const ones = [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
  ];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  if (n < 20) return ones[n];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const r = n % 10;
    return `${tens[t]}${r ? `-${ones[r]}` : ''}`;
  }
  const h = Math.floor(n / 100);
  const r = n % 100;
  return `${ones[h]} hundred${r ? ` and ${numberToWordsUnderThousand(r)}` : ''}`;
}

function numberToWords(n) {
  if (n === 0) return 'zero';
  const parts = [];
  const billions = Math.floor(n / 1_000_000_000);
  const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;

  if (billions) parts.push(`${numberToWordsUnderThousand(billions)} billion`);
  if (millions) parts.push(`${numberToWordsUnderThousand(millions)} million`);
  if (thousands) parts.push(`${numberToWordsUnderThousand(thousands)} thousand`);
  if (rest) parts.push(numberToWordsUnderThousand(rest));

  return parts.join(' ');
}

function amountToWordsAED(amount) {
  const value = Math.max(0, Number(amount) || 0);
  const dirhams = Math.floor(value);
  const fils = Math.round((value - dirhams) * 100);

  const dirhamWords = numberToWords(dirhams);
  const capitalized =
    dirhamWords.charAt(0).toUpperCase() + dirhamWords.slice(1);

  if (fils > 0) {
    const filsWords = numberToWords(fils);
    return `${capitalized} UAE dirhams and ${filsWords} fils`;
  }

  return `${capitalized} UAE dirhams`;
}

function statusToCurrentStage(status) {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'Request Created';
  if (s === 'rfq sent') return 'RFQ Sent';
  if (s === 'offers received') return 'RFQ Sent';
  if (s === 'supplier selected') return 'Supplier Selected';
  if (s === 'converted to order') return 'Order Created';
  if (s === 'closed') return 'Paid';
  return 'Request Created';
}

// ---------- Main component ----------

export default function Procurement() {
  const [activeTab, setActiveTab] = useState('requests'); // requests | orders | suppliers | rfq

  // Requests state (loaded from Supabase)
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestError, setRequestError] = useState(null);
  const [requestSearch, setRequestSearch] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('');
  const [requestDeptFilter, setRequestDeptFilter] = useState('');
  const [requestByFilter, setRequestByFilter] = useState('');
  const [requestSupplierFilter, setRequestSupplierFilter] = useState('');
  const [requestFrom, setRequestFrom] = useState('');
  const [requestTo, setRequestTo] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestDrawer, setShowRequestDrawer] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  /** When true, PR / PO / RFQ PDF downloads omit stamp and signature images (empty boxes remain). */
  const [prPdfOmitStampSignature, setPrPdfOmitStampSignature] = useState(false);
  const [poPdfOmitStampSignature, setPoPdfOmitStampSignature] = useState(false);
  const [rfqPdfOmitStampSignature, setRfqPdfOmitStampSignature] = useState(false);

  const requestDepartments = useMemo(
    () => [...new Set(requests.map((r) => r.departmentProject))],
    [requests],
  );
  const requestPeople = useMemo(
    () => [...new Set(requests.map((r) => r.requestedBy))],
    [requests],
  );
  const requestSuppliers = useMemo(
    () => [...new Set(requests.map((r) => r.selectedSupplier).filter(Boolean))],
    [requests],
  );

  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const q = requestSearch.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (r.requestId || '').toLowerCase().includes(q) ||
        (r.title || '').toLowerCase().includes(q) ||
        (r.departmentProject || '').toLowerCase().includes(q) ||
        (r.requestedBy || '').toLowerCase().includes(q);
      const matchesStatus = !requestStatusFilter || r.status === requestStatusFilter;
      const matchesDept = !requestDeptFilter || r.departmentProject === requestDeptFilter;
      const matchesBy = !requestByFilter || r.requestedBy === requestByFilter;
      const matchesSupplier =
        !requestSupplierFilter || r.selectedSupplier === requestSupplierFilter;
      const d = r.neededDate || '';
      const matchesFrom = !requestFrom || (d && d >= requestFrom);
      const matchesTo = !requestTo || (d && d <= requestTo);
      return (
        matchesSearch &&
        matchesStatus &&
        matchesDept &&
        matchesBy &&
        matchesSupplier &&
        matchesFrom &&
        matchesTo
      );
    });
  }, [
    requests,
    requestSearch,
    requestStatusFilter,
    requestDeptFilter,
    requestByFilter,
    requestSupplierFilter,
    requestFrom,
    requestTo,
  ]);

  // Add Request form (simple draft)
  const [newRequest, setNewRequest] = useState({
    title: '',
    departmentId: '',
    projectId: '',
    departmentProject: '',
    requestedBy: '',
    neededDate: '',
    priority: 'Medium',
    notes: '',
    status: 'Draft',
    suppliersInvited: 0,
    invitedSupplierIds: [],
    sendRfqOnSave: false,
    sendEmailOnUpdate: false,
    selectedSupplier: '',
  });
  const [newItems, setNewItems] = useState([
    { id: 1, item: '', quantity: '', specification: '', estimatedCost: '' },
  ]);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      procurementSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        procurementSupabase
          .from('procurement_requests')
          // Narrow the selected columns to avoid timeouts on large datasets.
          .select(`
            id,
            request_no,
            title,
            department_project,
            requested_by,
            needed_date,
            suppliers_invited_count,
            status,
            suppliers(company_name)
          `)
          .order('created_at', { ascending: false })
          .limit(100),
        timeoutPromise,
      ]);
      const { data, error } = res;
      if (error) {
        console.warn('[Procurement] fetchRequests', error);
        setRequestError(error.message);
        setRequests([]);
      } else {
        setRequests(
          (data ?? []).map((r) => ({
            id: r.id,
            requestId: r.request_no,
            title: r.title,
            departmentProject: r.department_project ?? '',
            requestedBy: r.requested_by ?? '',
            neededDate: r.needed_date ?? '',
            suppliersInvited: r.suppliers_invited_count ?? 0,
            selectedSupplier: r.suppliers?.company_name ?? '',
            status: r.status ?? 'Draft',
          })),
        );
      }
    } catch (e) {
      const msg = String(e?.message || '');
      // This timeout is non-blocking; suppress the visible error noise.
      if (msg.includes('Request timed out. Try refreshing.')) {
        setRequests([]);
        return;
      }
      setRequestError(msg || 'Failed to load');
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);

  const fetchDepartments = useCallback(async () => {
    const { data, error } = await procurementSupabase
      .from('departments')
      .select('id, name')
      .order('name', { ascending: true });
    if (!error) setDepartments(data ?? []);
  }, []);

  const fetchProjects = useCallback(async () => {
    const { data, error } = await procurementSupabase
      .from('projects')
      .select('id, title')
      .order('title', { ascending: true });
    if (!error) setProjects(data ?? []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { error: sessionErr } = await procurementSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setRequestError(sessionErr.message);
        setRequestsLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
        );
        await Promise.race([
          Promise.all([
            fetchRequests(),
            fetchDepartments(),
            fetchProjects(),
          ]),
          timeoutPromise,
        ]);
      } catch (e) {
        if (!cancelled) {
          const msg = String(e?.message || '');
          if (msg.includes('Request timed out. Try refreshing.')) {
            setRequests([]);
            return;
          }
          setRequestError(msg || 'Failed to load');
          setRequests([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchRequests, fetchDepartments, fetchProjects]);

  const getDepartmentProjectLabel = () => {
    const dept = departments.find((d) => d.id === newRequest.departmentId);
    const proj = projects.find((p) => p.id === newRequest.projectId);
    const parts = [dept?.name, proj?.title].filter(Boolean);
    return parts.join(' / ') || '';
  };

  const handleAddItemRow = () => {
    setNewItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        item: '',
        quantity: '',
        specification: '',
        estimatedCost: '',
      },
    ]);
  };

  const handleItemChange = (id, key, value) => {
    setNewItems((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  };

  const toggleSupplierForNewRequest = (supplierId) => {
    setNewRequest((prev) => {
      // Ensure this is always an array before feeding it to Set()/spread.
      const invited = Array.isArray(prev.invitedSupplierIds) ? prev.invitedSupplierIds : [];
      const current = new Set(invited);
      if (current.has(supplierId)) {
        current.delete(supplierId);
      } else {
        current.add(supplierId);
      }
      const invitedSupplierIds = Array.from(current);
      return {
        ...prev,
        invitedSupplierIds,
        suppliersInvited: invitedSupplierIds.length,
      };
    });
  };

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const raw = typeof reader.result === 'string' ? reader.result : '';
        const base64 = raw.includes(',') ? raw.split(',')[1] : raw;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function buildRequestPdfAttachment({ requestNo, title, departmentProject, requestedBy, neededDate, priority, notes, lineItems }) {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const margin = 12;
    let y = 16;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Request for Quotation (RFQ)', margin, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(`Request No: ${requestNo || '—'}`, margin, y); y += 6;
    pdf.text(`Title: ${title || '—'}`, margin, y); y += 6;
    pdf.text(`Department: ${departmentLabelForPrPdf(departmentProject) || '—'}`, margin, y); y += 6;
    pdf.text(`Requested by: ${requestedBy || '—'}`, margin, y); y += 6;
    pdf.text(`Needed date: ${neededDate || '—'}`, margin, y); y += 6;
    pdf.text(`Priority: ${priority || '—'}`, margin, y); y += 8;
    if ((notes || '').trim()) {
      pdf.text(`Notes: ${notes.trim()}`, margin, y, { maxWidth: 180 });
      y += 12;
    }
    const body = (lineItems || [])
      .filter((row) => (row.item || '').trim() || (Number(row.quantity) || 0) > 0)
      .map((row, idx) => [
        String(idx + 1),
        (row.item || '').trim() || '—',
        String(row.quantity ?? '—'),
        (row.specification || '').trim() || '—',
      ]);
    autoTable(pdf, {
      startY: y,
      head: [['#', 'Item', 'Qty', 'Specification']],
      body: body.length ? body : [['1', '—', '—', '—']],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
      margin: { left: margin, right: margin },
    });
    const blob = pdf.output('blob');
    return {
      filename: `${(requestNo || 'procurement-request').replace(/[^\w.-]+/g, '_')}.pdf`,
      contentBase64: await blobToBase64(blob),
      mimeType: 'application/pdf',
    };
  }

  async function buildPurchaseOrderPdfAttachment({ orderNo, supplierName, orderDate, deliveryDate, paymentMode, paymentTerms, notes, lineItems, totalAmount }) {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const margin = 12;
    let y = 16;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Purchase Order', margin, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(`PO Number: ${orderNo || '—'}`, margin, y); y += 6;
    pdf.text(`Supplier: ${supplierName || '—'}`, margin, y); y += 6;
    pdf.text(`Order date: ${orderDate || '—'}`, margin, y); y += 6;
    pdf.text(`Delivery date: ${deliveryDate || '—'}`, margin, y); y += 6;
    pdf.text(`Payment mode: ${paymentMode || '—'}`, margin, y); y += 6;
    pdf.text(`Payment terms: ${paymentTerms || '—'}`, margin, y); y += 8;
    if ((notes || '').trim()) {
      pdf.text(`Notes: ${notes.trim()}`, margin, y, { maxWidth: 180 });
      y += 12;
    }
    const body = (lineItems || [])
      .filter((row) => (row.description || '').trim() && (Number(row.qty) || 0) > 0)
      .map((row, idx) => [
        String(idx + 1),
        (row.description || '').trim(),
        String(row.qty ?? '—'),
        (row.unit || '').trim() || '—',
        String(row.unitPrice ?? '—'),
      ]);
    autoTable(pdf, {
      startY: y,
      head: [['#', 'Description', 'Qty', 'Unit', 'Unit price (OMR)']],
      body: body.length ? body : [['1', '—', '—', '—', '—']],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
      margin: { left: margin, right: margin },
    });
    const totalY = (pdf.lastAutoTable?.finalY || y) + 8;
    pdf.text(`Estimated total: ${totalAmount != null ? `${totalAmount} OMR` : '—'}`, margin, totalY);
    const blob = pdf.output('blob');
    return {
      filename: `${(orderNo || 'purchase-order').replace(/[^\w.-]+/g, '_')}.pdf`,
      contentBase64: await blobToBase64(blob),
      mimeType: 'application/pdf',
    };
  }

  const handleDownloadRequestPdf = async (requestOrNull, items = []) => {
    const req = requestOrNull || {
      requestId: 'Draft',
      title: newRequest.title,
      departmentProject: getDepartmentProjectLabel() || newRequest.departmentProject,
      requestedBy: newRequest.requestedBy,
      neededDate: newRequest.neededDate,
      status: newRequest.status,
    };
    const rows = (items || []).filter((r) => (r.item || '').trim() || (r.quantity || '').toString().trim());
    const headerPromise = loadImageDataUrl(headImgUrl);
    let stampDataUrl = null;
    let signatureDataUrl = null;
    if (!prPdfOmitStampSignature) {
      const { stampUrl, signatureUrl } = await getDefaultStampAndSignature();
      [stampDataUrl, signatureDataUrl] = await Promise.all([
        stampUrl ? loadImageDataUrl(stampUrl) : Promise.resolve(null),
        signatureUrl ? loadImageDataUrl(signatureUrl) : Promise.resolve(null),
      ]);
    }
    const headerDataUrl = await headerPromise;

    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = PDF_MARGIN_MM;

    if (headerDataUrl) {
      pdf.addImage(headerDataUrl, 'PNG', 0, 0, pageW, PDF_HEADER_HEIGHT_MM);
    }

    let y = PDF_HEADER_HEIGHT_MM + 8;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const txt = (s) => (s ?? '').toString();
    pdf.text(`Request No: ${txt(req.requestId || '—')}`, margin, y);
    pdf.text(`Date: ${txt(req.neededDate || new Date().toISOString().slice(0, 10))}`, margin + 95, y);
    y += 6;
    pdf.text(`Title: ${txt(req.title || '—')}`, margin, y);
    pdf.text(`Status: ${txt(req.status || '—')}`, margin + 95, y);
    y += 6;
    pdf.text(`Department: ${txt(departmentLabelForPrPdf(req.departmentProject) || '—')}`, margin, y);
    pdf.text(`Requested by: ${txt(req.requestedBy || '—')}`, margin + 95, y);
    y += 10;

    const tableBody = (rows.length ? rows : [{ item: '—', quantity: '', specification: '', estimatedCost: '' }]).map(
      (row, idx) => [
        String(rows.length ? idx + 1 : 1),
        txt(row.item),
        txt(row.quantity ?? ''),
        '—',
        txt(row.specification),
        txt(row.estimatedCost ?? ''),
      ],
    );

    autoTable(pdf, {
      head: [['#', 'Item', 'Quantity', 'Unit', 'Specification', 'Est. cost']],
      body: tableBody,
      startY: y,
      margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
      tableWidth: pageW - margin * 2,
      showHead: 'everyPage',
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 18 },
        3: { cellWidth: 12 },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 22 },
      },
      didDrawPage: () => {
        drawPdfHeaderFooter(pdf, pageW, pageH, headerDataUrl);
      },
    });

    y = pdf.lastAutoTable?.finalY ?? y;
    y += 14;

    const boxW = (pageW - margin * 2 - 15) / 2;
    const boxH = 28;
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.3);
    pdf.rect(margin, y, boxW, boxH, 'S');
    pdf.rect(margin + boxW + 15, y, boxW, boxH, 'S');
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 128);
    pdf.text('Authorized Signature', margin + boxW / 2, y + 5, { align: 'center' });
    if (!prPdfOmitStampSignature && signatureDataUrl) {
      try {
        pdf.addImage(signatureDataUrl, 'PNG', margin + 4, y + 8, Math.min(boxW - 8, 45), Math.min(boxH - 12, 18));
      } catch {
        // ignore
      }
    }
    const prStampLeft = margin + boxW + 19;
    const prStampW = Math.min(boxW - 8, 45);
    const prStampH = Math.min(boxH - 12, 18);
    const prStampY = y + 8;
    if (!prPdfOmitStampSignature && stampDataUrl) {
      try {
        pdf.addImage(stampDataUrl, 'PNG', prStampLeft, prStampY, prStampW, prStampH);
      } catch {
        // ignore
      }
    }
    pdf.setFont('helvetica', 'bold');
    pdf.text('Company Stamp', prStampLeft + prStampW / 2, prStampY + prStampH / 2 + 1.2, { align: 'center' });
    pdf.setFont('helvetica', 'normal');

    pdf.save(`procurement-request-${(req.requestId || 'Draft').replace(/</g, '_')}.pdf`);
  };

  const [editingRequestId, setEditingRequestId] = useState(null);
  const [selectedRequestItems, setSelectedRequestItems] = useState([]);
  const [requestSaving, setRequestSaving] = useState(false);

  const handleOpenNewRequest = () => {
    setEditingRequestId(null);
    setNewRequest({
      title: '',
      departmentId: '',
      projectId: '',
      departmentProject: '',
      requestedBy: '',
      neededDate: '',
      priority: 'Medium',
      notes: '',
      status: 'Draft',
      suppliersInvited: 0,
      invitedSupplierIds: [],
      sendRfqOnSave: false,
      sendEmailOnUpdate: false,
      selectedSupplier: '',
    });
    setNewItems([{ id: 1, item: '', quantity: '', specification: '', estimatedCost: '' }]);
    setShowRequestModal(true);
  };

  const handleOpenEditRequest = useCallback(async (req) => {
    setShowRequestDrawer(false);
    setEditingRequestId(req.id);
    setRequestError(null);
    await runWakeUp();
    const { data: reqRow, error: reqErr } = await procurementSupabase
      .from('procurement_requests')
      .select('*')
      .eq('id', req.id)
      .single();
    if (reqErr || !reqRow) {
      setRequestError(reqRow ? 'Request not found.' : reqErr?.message || 'Failed to load request.');
      return;
    }
    const { data: itemsRows } = await procurementSupabase
      .from('procurement_request_items')
      .select('*')
      .eq('request_id', req.id)
      .order('line_no', { ascending: true });
    const items = (itemsRows ?? []).length
      ? itemsRows.map((row, idx) => ({
          id: row.id || `item-${idx}`,
          item: row.item ?? '',
          quantity: String(row.quantity ?? ''),
          specification: row.specification ?? '',
          estimatedCost: row.estimated_cost != null ? String(row.estimated_cost) : '',
        }))
      : [{ id: 1, item: '', quantity: '', specification: '', estimatedCost: '' }];
    setNewRequest({
      title: reqRow.title ?? '',
      departmentId: '',
      projectId: '',
      departmentProject: reqRow.department_project ?? '',
      requestedBy: reqRow.requested_by ?? '',
      neededDate: reqRow.needed_date ?? '',
      priority: reqRow.priority ?? 'Medium',
      notes: reqRow.notes ?? '',
      status: reqRow.status ?? 'Draft',
      invitedSupplierIds: Array.isArray(reqRow.invited_supplier_ids) ? reqRow.invited_supplier_ids : [],
      suppliersInvited: (reqRow.invited_supplier_ids?.length ?? 0),
      sendRfqOnSave: false,
      sendEmailOnUpdate: false,
      selectedSupplier: req.selectedSupplier ?? '',
    });
    setNewItems(items);
    setShowRequestModal(true);
  }, [runWakeUp]);

  const handleSaveNewRequest = async () => {
    if (!newRequest.title.trim()) {
      setShowRequestModal(false);
      return;
    }
    const departmentProjectLabel =
      getDepartmentProjectLabel().trim() || newRequest.departmentProject?.trim() || null;
    const isEdit = !!editingRequestId;
    const selectedSupplierId = newRequest.selectedSupplier
      ? (suppliers.find(
          (s) => (s.company_name || s.companyName) === newRequest.selectedSupplier,
        )?.id ?? null)
      : null;

    setRequestError(null);
    setRequestSaving(true);
    try {
      await runWakeUp();
      if (isEdit) {
        const { error: updateErr } = await procurementSupabase
          .from('procurement_requests')
          .update({
            title: newRequest.title.trim(),
            department_project: departmentProjectLabel,
            requested_by: newRequest.requestedBy?.trim() || null,
            needed_date: newRequest.neededDate || null,
            priority: newRequest.priority || 'Medium',
            notes: newRequest.notes?.trim() || null,
            status: newRequest.status || 'Draft',
            invited_supplier_ids: Array.isArray(newRequest.invitedSupplierIds) ? newRequest.invitedSupplierIds : [],
            selected_supplier_id: selectedSupplierId,
          })
          .eq('id', editingRequestId);
        if (updateErr) throw updateErr;

        await procurementSupabase
          .from('procurement_request_items')
          .delete()
          .eq('request_id', editingRequestId);

        const validItems = (newItems || []).filter(
          (row) => (row.item || '').trim() && (Number(row.quantity) || 0) > 0,
        );
        if (validItems.length > 0) {
          const rows = validItems.map((row, idx) => ({
            request_id: editingRequestId,
            line_no: idx + 1,
            item: (row.item || '').trim(),
            specification: (row.specification || '').trim() || null,
            quantity: Math.max(0, Number(row.quantity) || 0),
            estimated_cost: Number(row.estimatedCost) || null,
          }));
          const { error: itemsError } = await procurementSupabase
            .from('procurement_request_items')
            .insert(rows);
          if (itemsError) throw itemsError;
        }

        if (newRequest.sendEmailOnUpdate && (newRequest.invitedSupplierIds?.length ?? 0) > 0) {
          try {
            const attachment = await buildRequestPdfAttachment({
              requestNo: newRequest.requestId || '',
              title: newRequest.title.trim(),
              departmentProject: departmentProjectLabel || '',
              requestedBy: newRequest.requestedBy?.trim() || '',
              neededDate: newRequest.neededDate || '',
              priority: newRequest.priority || 'Medium',
              notes: newRequest.notes?.trim() || '',
              lineItems: newItems,
            });
            const { skipped, errors } = await sendProcurementSupplierEmailsFromClient({
              type: 'update',
              recipientSupplierIds: newRequest.invitedSupplierIds,
              suppliersList: suppliers,
              requestNo: newRequest.requestId || '',
              title: newRequest.title.trim(),
              departmentProject: departmentProjectLabel || '',
              requestedBy: newRequest.requestedBy?.trim() || '',
              neededDate: newRequest.neededDate || '',
              lineItems: newItems,
              notes: newRequest.notes?.trim() || '',
              priority: newRequest.priority || 'Medium',
              attachment,
            });

            if (skipped > 0 || errors.length) {
              console.warn('[Procurement] Partial supplier email:', { skipped, errors });
            }
          } catch (emailErr) {
            console.warn('[Procurement] send email', emailErr);
            setRequestError(
              `Request saved. Supplier email could not be sent: ${emailErr?.message || 'unknown error'}.`,
            );
          }
        }
      } else {
        const year = new Date().getFullYear();
        const existingNumbers = requests
          .filter((r) => r.requestId && r.requestId.startsWith(`PR-${year}-`))
          .map((r) => {
            const m = (r.requestId || '').match(new RegExp(`PR-\\d{4}-(\\d+)`));
            return m ? parseInt(m[1], 10) : 0;
          });
        const maxNo = Math.max(0, ...existingNumbers);
        const requestNo = `PR-${year}-${String(maxNo + 1).padStart(3, '0')}`;
        const finalStatus = newRequest.sendRfqOnSave
          ? (newRequest.status === 'Draft' ? 'RFQ Sent' : newRequest.status)
          : newRequest.status || 'Draft';

        const { data: inserted, error: reqError } = await procurementSupabase
          .from('procurement_requests')
          .insert({
            request_no: requestNo,
            title: newRequest.title.trim(),
            department_project: departmentProjectLabel,
            requested_by: newRequest.requestedBy?.trim() || null,
            needed_date: newRequest.neededDate || null,
            priority: newRequest.priority || 'Medium',
            notes: newRequest.notes?.trim() || null,
            status: finalStatus,
            invited_supplier_ids: Array.isArray(newRequest.invitedSupplierIds) ? newRequest.invitedSupplierIds : [],
            selected_supplier_id: selectedSupplierId,
          })
          .select('id')
          .single();
        if (reqError) throw reqError;

        const validItems = (newItems || []).filter(
          (row) => (row.item || '').trim() && (Number(row.quantity) || 0) > 0,
        );
        if (validItems.length > 0) {
          const rows = validItems.map((row, idx) => ({
            request_id: inserted.id,
            line_no: idx + 1,
            item: (row.item || '').trim(),
            specification: (row.specification || '').trim() || null,
            quantity: Math.max(0, Number(row.quantity) || 0),
            estimated_cost: Number(row.estimatedCost) || null,
          }));
          const { error: itemsError } = await procurementSupabase
            .from('procurement_request_items')
            .insert(rows);
          if (itemsError) throw itemsError;
        }

        if (newRequest.sendRfqOnSave && (newRequest.invitedSupplierIds?.length ?? 0) > 0) {
          try {
            const attachment = await buildRequestPdfAttachment({
              requestNo,
              title: newRequest.title.trim(),
              departmentProject: departmentProjectLabel || '',
              requestedBy: newRequest.requestedBy?.trim() || '',
              neededDate: newRequest.neededDate || '',
              priority: newRequest.priority || 'Medium',
              notes: newRequest.notes?.trim() || '',
              lineItems: newItems,
            });
            const { skipped, errors } = await sendProcurementSupplierEmailsFromClient({
              type: 'rfq',
              recipientSupplierIds: newRequest.invitedSupplierIds,
              suppliersList: suppliers,
              requestNo,
              title: newRequest.title.trim(),
              departmentProject: departmentProjectLabel || '',
              requestedBy: newRequest.requestedBy?.trim() || '',
              neededDate: newRequest.neededDate || '',
              lineItems: newItems,
              notes: newRequest.notes?.trim() || '',
              priority: newRequest.priority || 'Medium',
              attachment,
            });
            if (skipped > 0 || errors.length) {
              console.warn('[Procurement] Partial RFQ supplier email:', { skipped, errors });
            }
          } catch (emailErr) {
            console.warn('[Procurement] send RFQ email', emailErr);
          }
        }

        // Internal notification: procurement request created
        try {
          const reqEmailRes = await sendProcurementRequestCreatedEmail({
            requestNo,
            title: newRequest.title.trim(),
            departmentProject: departmentProjectLabel,
            requestedBy: newRequest.requestedBy?.trim() || '',
            neededDate: newRequest.neededDate || '',
            priority: newRequest.priority || 'Medium',
            status: finalStatus,
            invitedCount: Array.isArray(newRequest.invitedSupplierIds)
              ? newRequest.invitedSupplierIds.length
              : 0,
          });
          if (!reqEmailRes.success && !reqEmailRes.skipped) {
            console.warn('[Procurement] request created email failed:', reqEmailRes.error);
          }
        } catch (emailErr) {
          console.warn('[Procurement] request created email exception:', emailErr);
        }
      }

      await fetchRequests();
      setShowRequestModal(false);
      setEditingRequestId(null);
    } catch (e) {
      console.warn('[Procurement] saveRequest', e);
      setRequestError(e?.message || 'Failed to save request.');
    } finally {
      setRequestSaving(false);
    }
  };

  const handleOpenRequestDetails = useCallback(async (req) => {
    setSelectedRequest(req);
    setShowRequestDrawer(true);
    setSelectedRequestItems([]);
    await runWakeUp();
    const { data } = await procurementSupabase
      .from('procurement_request_items')
      .select('*')
      .eq('request_id', req.id)
      .order('line_no', { ascending: true });
    setSelectedRequestItems(Array.isArray(data) ? data : []);
  }, [runWakeUp]);

  const handleConfirmDeleteRequest = async () => {
    if (!requestToDelete) return;
    setRequestError(null);
    try {
      await runWakeUp();
      const { error } = await procurementSupabase
        .from('procurement_requests')
        .delete()
        .eq('id', requestToDelete.id);
      if (error) throw error;
      await fetchRequests();
      setRequestToDelete(null);
    } catch (e) {
      console.warn('[Procurement] deleteRequest', e);
      setRequestError(e?.message || 'Failed to delete request.');
    }
  };

  // Orders state (from Supabase)
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orderError, setOrderError] = useState(null);
  const [orderPdfError, setOrderPdfError] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDrawer, setShowOrderDrawer] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);
  const [newOrder, setNewOrder] = useState({
    supplierId: '',
    linkedRequestId: '',
    poRef: '',
    refQuotation: '',
    orderDate: '',
    deliveryDate: '',
    shippingCost: '',
    paymentMode: '',
    paymentTerms: '',
    notes: '',
    orderedBy: '',
    approvedBy: '',
    sendPoEmailToSupplier: true,
  });
  const [newOrderItems, setNewOrderItems] = useState([
    { id: 1, description: '', qty: '', unit: '', unitPrice: '' },
  ]);

  const [rfqRecords, setRfqRecords] = useState([]);
  const [rfqLoading, setRfqLoading] = useState(true);
  const [rfqError, setRfqError] = useState(null);
  const [rfqSearch, setRfqSearch] = useState('');
  const [showRfqModal, setShowRfqModal] = useState(false);
  const [editingRfqId, setEditingRfqId] = useState(null);
  const [rfqForm, setRfqForm] = useState(() => createEmptyRfqForm());
  const [rfqModalError, setRfqModalError] = useState(null);
  const [rfqSaving, setRfqSaving] = useState(false);
  const [rfqToDelete, setRfqToDelete] = useState(null);
  const [rfqDeleteError, setRfqDeleteError] = useState(null);
  const [rfqPdfError, setRfqPdfError] = useState(null);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrderError(null);
    await runWakeUp();
    const { data, error } = await procurementSupabase
      .from('purchase_orders')
      .select(`
        id,
        order_no,
        total_amount,
        order_date,
        delivery_status,
        payment_status,
        po_ref,
        ref_quotation,
        delivery_date,
        shipping_cost,
        tax_percent,
        payment_mode,
        payment_terms,
        notes,
        ordered_by,
        approved_by,
        invoice_file_name,
        invoice_amount,
        invoice_date,
        suppliers(company_name, primary_phone, primary_email, location, contacts),
        procurement_requests(request_no, title),
        purchase_order_items(
          id,
          line_no,
          description,
          quantity,
          unit,
          unit_price,
          amount
        )
      `)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) {
      console.warn('[Procurement] fetchOrders', error);
      setOrderError(error.message);
      setOrders([]);
    } else {
      setOrders(
        (Array.isArray(data) ? data : []).map((row) => {
          const items = (row.purchase_order_items ?? [])
            .sort((a, b) => (a.line_no ?? 0) - (b.line_no ?? 0))
            .map((i) => {
              const qty = Number(i.quantity) || 0;
              const unitPrice = Number(i.unit_price) || 0;
              const amt = i.amount != null ? Number(i.amount) : qty * unitPrice;
              return {
                description: i.description,
                quantity: i.quantity,
                unit: i.unit,
                amount: amt,
              };
            });
          const inv =
            row.invoice_file_name != null ||
            row.invoice_amount != null ||
            row.invoice_date != null
              ? {
                  fileName: row.invoice_file_name,
                  amount: row.invoice_amount,
                  date: row.invoice_date,
                }
              : null;
          return {
            id: row.id,
            orderId: row.order_no,
            supplier: row.suppliers?.company_name ?? '',
            linkedRequestId: row.procurement_requests?.request_no ?? '',
            linkedRequestTitle: row.procurement_requests?.title ?? '',
            totalAmount: Number(row.total_amount) ?? 0,
            orderDate: row.order_date ?? '',
            deliveryStatus: row.delivery_status ?? 'Pending',
            paymentStatus: row.payment_status ?? 'Unpaid',
            supplierContact: getPrimaryContact(row.suppliers)?.name ?? '',
            supplierPhone: row.suppliers?.primary_phone ?? '',
            supplierEmail: row.suppliers?.primary_email ?? '',
            supplierLocation: row.suppliers?.location ?? '',
            poRef: row.po_ref ?? '',
            refQuotation: row.ref_quotation ?? '',
            deliveryDate: row.delivery_date ?? '',
            shippingCost: Number(row.shipping_cost) ?? 0,
            taxPercent: Number(row.tax_percent) ?? 0,
            paymentMode: row.payment_mode ?? '',
            paymentTerms: row.payment_terms ?? '',
            notes: row.notes ?? '',
            orderedBy: row.ordered_by ?? '',
            approvedBy: row.approved_by ?? '',
            items,
            invoice: inv,
          };
        }),
      );
    }
    setOrdersLoading(false);
  }, [runWakeUp]);

  const fetchRfqs = useCallback(async () => {
    setRfqLoading(true);
    setRfqError(null);
    await runWakeUp();
    const { data, error } = await procurementSupabase
      .from('rfqs')
      .select(
        `
        id,
        ref_no,
        document_date,
        client,
        client_country,
        subject,
        tender_no,
        warranty,
        delivery,
        status,
        subtotal_amount,
        tax_amount,
        grand_total,
        created_at,
        rfq_line_items (
          id,
          line_no,
          item_name,
          description,
          quantity,
          unit_price
        )
      `,
      )
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) {
      console.warn('[Procurement] fetchRfqs', error);
      setRfqError(error.message);
      setRfqRecords([]);
    } else {
      setRfqRecords((Array.isArray(data) ? data : []).map(mapRfqRecordFromDb).filter(Boolean));
    }
    setRfqLoading(false);
  }, [runWakeUp]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchRfqs();
  }, [fetchRfqs]);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    return orders.filter((o) => {
      if (!q) return true;
      return (
        (o.orderId || '').toLowerCase().includes(q) ||
        (o.supplier || '').toLowerCase().includes(q) ||
        (o.linkedRequestId || '').toLowerCase().includes(q) ||
        (o.linkedRequestTitle || '').toLowerCase().includes(q)
      );
    });
  }, [orders, orderSearch]);

  const handleOpenCreateOrder = () => {
    setOrderPdfError(null);
    setNewOrder({
      supplierId: '',
      linkedRequestId: '',
      poRef: '',
      refQuotation: '',
      orderDate: '',
      deliveryDate: '',
      shippingCost: '',
      paymentMode: '',
      paymentTerms: '',
      notes: '',
      orderedBy: '',
      approvedBy: '',
      sendPoEmailToSupplier: true,
    });
    setNewOrderItems([{ id: 1, description: '', qty: '', unit: '', unitPrice: '' }]);
    setShowCreateOrderModal(true);
  };

  const handleAddOrderItemRow = () => {
    setNewOrderItems((prev) => [
      ...prev,
      { id: Date.now(), description: '', qty: '', unit: '', unitPrice: '' },
    ]);
  };

  const handleOrderItemChange = (id, key, value) => {
    setNewOrderItems((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
    );
  };

  const calculateNewOrderTotal = () =>
    newOrderItems.reduce((sum, row) => {
      const qty = Number(row.qty) || 0;
      const price = Number(row.unitPrice) || 0;
      return sum + qty * price;
    }, 0);

  const handleDownloadPoDoc = async (orderFromDrawer = null) => {
    setOrderPdfError(null);
    const isFromDrawer = orderFromDrawer != null;
    const supplier = isFromDrawer
      ? null
      : suppliers.find((s) => s.id === newOrder.supplierId);
    const request = isFromDrawer
      ? null
      : requests.find((r) => r.id === newOrder.linkedRequestId);

    try {
    let poItems;
    let subtotal;
    let shippingCost;
    let taxAmount;
    let grandTotal;
    let amountWords;
    let poRef;
    let refQuotation;
    let companyName;
    let contactName;
    let contactPhone;
    let location;
    let orderDate;
    let paymentMode;
    let paymentTerms;
    let notes;
    const today = new Date().toISOString().slice(0, 10);

    if (isFromDrawer) {
      const o = orderFromDrawer;
      const q = (r) => Number(r.quantity) || 0;
      const a = (r) => Number(r.amount) || 0;
      poItems = (o.items ?? []).map((row, idx) => {
        const qty = q(row);
        const amt = a(row);
        const unitPrice = qty > 0 ? amt / qty : amt;
        return [idx + 1, row.description || '', row.unit || '', qty || '', unitPrice ? unitPrice.toFixed(2) : '', amt ? amt.toFixed(2) : ''];
      });
      if (!poItems.length) poItems = [[1, '—', '—', '—', '—', '—']];
      subtotal = (o.items ?? []).reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      shippingCost = Number(o.shippingCost) || 0;
      ({ taxAmount, grandTotal } = computePoTaxAndGrandTotal(subtotal, shippingCost));
      amountWords = grandTotal ? amountToWordsAED(grandTotal) : '';
      poRef = o.poRef || o.orderId || '______________';
      refQuotation = o.refQuotation || '____________________________';
      companyName = o.supplier || '____________________________';
      contactName = o.supplierContact || '____________________________';
      contactPhone = o.supplierPhone || '______________';
      location = o.supplierLocation || '______________';
      orderDate = o.orderDate || today;
      paymentMode = o.paymentMode || '';
      paymentTerms = o.paymentTerms || '____________________________';
      notes = o.notes || '____________________________________________';
    } else {
      poItems =
        newOrderItems && newOrderItems.length
          ? newOrderItems.map((row, idx) => {
              const qty = Number(row.qty) || 0;
              const unitPrice = Number(row.unitPrice) || 0;
              const lineTotal = qty * unitPrice;
              return [
                idx + 1,
                row.description || '____________________________',
                row.unit || '',
                qty || '',
                unitPrice ? unitPrice.toFixed(2) : '',
                lineTotal ? lineTotal.toFixed(2) : '',
              ];
            })
          : [[1, '______________________________________________', '______', '______', '______', '______']];
      subtotal = calculateNewOrderTotal() || 0;
      shippingCost = Math.max(0, Number(newOrder.shippingCost) || 0);
      ({ taxAmount, grandTotal } = computePoTaxAndGrandTotal(subtotal, shippingCost));
      amountWords = grandTotal ? amountToWordsAED(grandTotal) : '';
      poRef = newOrder.poRef || request?.requestId || '______________';
      refQuotation = newOrder.refQuotation || '____________________________';
      companyName = supplier?.company_name || '____________________________';
      const primaryContact = supplier ? getPrimaryContact(supplier) : null;
      contactName = primaryContact?.name || '____________________________';
      contactPhone = supplier?.primary_phone || primaryContact?.phone || '______________';
      location = supplier?.location || '______________';
      orderDate = newOrder.orderDate || today;
      paymentMode = newOrder.paymentMode || '';
      paymentTerms = newOrder.paymentTerms || '____________________________';
      notes = newOrder.notes || '____________________________________________';
    }

    const headerPromise = loadImageDataUrl(headImgUrl);
    let stampDataUrl = null;
    let signatureDataUrl = null;
    if (!poPdfOmitStampSignature) {
      const { stampUrl, signatureUrl } = await getDefaultStampAndSignature();
      [stampDataUrl, signatureDataUrl] = await Promise.all([
        stampUrl ? loadImageDataUrl(stampUrl) : Promise.resolve(null),
        signatureUrl ? loadImageDataUrl(signatureUrl) : Promise.resolve(null),
      ]);
    }
    const headerDataUrl = await headerPromise;

    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = PDF_MARGIN_MM;

    if (headerDataUrl) {
      pdf.addImage(headerDataUrl, 'PNG', 0, 0, pageW, PDF_HEADER_HEIGHT_MM);
    }

    let y = PDF_HEADER_HEIGHT_MM + 8;
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PURCHASE ORDER', pageW / 2, y, { align: 'center' });
    y += 10;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    const metaW = (pageW - margin * 2 - 8) / 2;
    const metaLeft = margin;
    const metaRight = margin + metaW + 8;
    const yMetaStart = y;
    const leftRows = [
      ['Purchase order ref', poRef],
      ['Company name', companyName],
      ['Contact person', contactName],
      ['Ref quotation', refQuotation],
    ];
    const rightRows = [
      ['Location', location],
      ['Contact', contactPhone],
      ['Date', orderDate],
    ];
    leftRows.forEach(([label, val], i) => {
      const yy = yMetaStart + i * 5;
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, metaLeft, yy, { maxWidth: metaW - 4 });
      pdf.setFont('helvetica', 'normal');
      pdf.text(String(val ?? '—'), metaLeft + 42, yy, { maxWidth: metaW - 46 });
    });
    rightRows.forEach(([label, val], i) => {
      const yy = yMetaStart + i * 5;
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, metaRight, yy, { maxWidth: metaW - 4 });
      pdf.setFont('helvetica', 'normal');
      pdf.text(String(val ?? '—'), metaRight + 38, yy, { maxWidth: metaW - 42 });
    });
    y = yMetaStart + 22;

    autoTable(pdf, {
      head: [['SR NO', 'Item description', 'Units', 'QTY', 'Unit price', 'Total price']],
      body: poItems.map((row) => row.map(String)),
      startY: y,
      margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
      tableWidth: pageW - margin * 2,
      showHead: 'everyPage',
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 18 },
        3: { cellWidth: 14 },
        4: { cellWidth: 22 },
        5: { cellWidth: 22 },
      },
      didDrawPage: () => {
        drawPdfHeaderFooter(pdf, pageW, pageH, headerDataUrl);
      },
    });

    y = pdf.lastAutoTable?.finalY ?? y;
    y += 8;

    autoTable(pdf, {
      body: [
        ['SUB TOTAL (OMR)', (subtotal ?? 0).toFixed(2)],
        ['SHIPPING COST', (shippingCost ?? 0).toFixed(2)],
        ['TAX (5% of subtotal)', (taxAmount ?? 0).toFixed(2)],
        ['TOTAL AMOUNT TO PAY (AED)', (grandTotal ?? 0).toFixed(2)],
        [amountWords || 'Amount in words: _______________________________', ''],
      ],
      startY: y,
      margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
      tableWidth: pageW - margin * 2,
      theme: 'plain',
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
    });
    y = pdf.lastAutoTable?.finalY ?? y;
    y += 6;

    pdf.setFontSize(9);
    const paymentModeLabels = { cash: 'Cash', cheque: 'Cheque (PDC)', bank: 'Bank', credit: 'Credit' };
    const paymentModeText = paymentModeLabels[paymentMode] || '—';
    pdf.text(`Mode of payment: ${paymentModeText}`, margin, y);
    y += 6;
    pdf.text(`Payment terms: ${paymentTerms}`, margin, y);
    y += 5;
    pdf.text(`Notes: ${notes}`, margin, y, { maxWidth: pageW - margin * 2 });
    y += 14;

    const boxGap = 4;
    const boxW = (pageW - margin * 2 - boxGap) / 2;
    const boxH = 32;
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.3);
    pdf.rect(margin, y, boxW, boxH, 'S');
    pdf.rect(margin + boxW + boxGap, y, boxW, boxH, 'S');
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 128);
    pdf.text('Authorized Signature', margin + boxW / 2, y + 5, { align: 'center' });
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(9);
    if (!poPdfOmitStampSignature && signatureDataUrl) {
      try {
        pdf.addImage(signatureDataUrl, 'PNG', margin + 4, y + 8, Math.min(boxW - 8, 35), Math.min(boxH - 12, 16));
      } catch {
        // ignore
      }
    }
    const stampBoxLeft = margin + boxW + boxGap;
    const stampImgW = Math.min(boxW - 8, 35);
    const stampImgH = Math.min(boxH - 12, 16);
    const stampImgY = y + 8;
    if (!poPdfOmitStampSignature && stampDataUrl) {
      try {
        pdf.addImage(
          stampDataUrl,
          'PNG',
          stampBoxLeft + (boxW - stampImgW) / 2,
          stampImgY,
          stampImgW,
          stampImgH,
        );
      } catch {
        // ignore
      }
    }
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 128);
    pdf.text('Company Stamp', stampBoxLeft + boxW / 2, stampImgY + stampImgH / 2 + 1.2, { align: 'center' });
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);

    pdf.save(`purchase-order-${(poRef || 'Draft').replace(/</g, '_')}.pdf`);
    } catch (e) {
      setOrderPdfError(e?.message || 'Failed to generate PDF.');
    }
  };

  const handleOpenOrderDetails = (order) => {
    setSelectedOrder(order);
    setOrderPdfError(null);
    setShowOrderDrawer(true);
  };

  const updateOrder = async (id, patch) => {
    const dbPatch = {};
    if (patch.deliveryStatus != null) dbPatch.delivery_status = patch.deliveryStatus;
    if (patch.paymentStatus != null) dbPatch.payment_status = patch.paymentStatus;
    if (patch.invoice != null) {
      if (patch.invoice.fileName != null) dbPatch.invoice_file_name = patch.invoice.fileName;
      if (patch.invoice.amount != null) dbPatch.invoice_amount = Number(patch.invoice.amount) || null;
      if (patch.invoice.date != null) dbPatch.invoice_date = patch.invoice.date || null;
    }
    if (Object.keys(dbPatch).length === 0) return;
    setOrderError(null);
    await runWakeUp();
    const { error } = await procurementSupabase.from('purchase_orders').update(dbPatch).eq('id', id);
    if (error) {
      console.warn('[Procurement] updateOrder', error);
      setOrderError(error.message);
      return;
    }
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, ...patch, invoice: { ...o.invoice, ...patch.invoice } } : o)),
    );
    if (selectedOrder && selectedOrder.id === id) {
      setSelectedOrder((prev) => ({ ...prev, ...patch, invoice: { ...prev.invoice, ...patch.invoice } }));
    }
  };

  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [supplierError, setSupplierError] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [newSupplier, setNewSupplier] = useState(emptySupplierForm());
  const [showQuickSupplierModal, setShowQuickSupplierModal] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);

  const fetchSuppliers = useCallback(async () => {
    setSuppliersLoading(true);
    setSupplierError(null);
    await runWakeUp();
    const { data, error } = await procurementSupabase
      .from('suppliers')
      .select('*')
      .order('company_name', { ascending: true, nullsFirst: false });
    if (error) {
      console.warn('[Procurement] fetchSuppliers', error);
      setSupplierError(error.message);
      setSuppliers(MOCK_SUPPLIERS);
    } else {
      setSuppliers(data ?? []);
    }
    setSuppliersLoading(false);
  }, [runWakeUp]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const refreshSessionAndFetchAll = useCallback(async () => {
    setRequestError(null);
    setOrderError(null);
    setSupplierError(null);
    setRfqError(null);
    try {
      await procurementSupabase.auth.getSession();
      await runWakeUp();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      await Promise.race([
        Promise.all([
          fetchRequests(),
          fetchDepartments(),
          fetchProjects(),
          fetchOrders(),
          fetchSuppliers(),
          fetchRfqs(),
        ]),
        timeoutPromise,
      ]);
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('Request timed out. Try refreshing.')) {
        return;
      }
      setRequestError(msg || 'Failed to load');
    }
  }, [runWakeUp, fetchRequests, fetchDepartments, fetchProjects, fetchOrders, fetchSuppliers, fetchRfqs]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetchAll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetchAll]);

  const filteredSuppliers = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    return suppliers.filter((s) => {
      if (!q) return true;
      return (
        (s.company_name || '').toLowerCase().includes(q) ||
        (s.industry || '').toLowerCase().includes(q) ||
        (s.category_products || '').toLowerCase().includes(q) ||
        (s.location || '').toLowerCase().includes(q)
      );
    });
  }, [suppliers, supplierSearch]);

  const handleOpenAddSupplier = () => {
    setEditingSupplier(null);
    setNewSupplier(emptySupplierForm());
    setSupplierError(null);
    setShowQuickSupplierModal(false);
    setShowSupplierModal(true);
  };

  const handleOpenQuickAddSupplier = () => {
    setEditingSupplier(null);
    setNewSupplier(emptySupplierForm());
    setSupplierError(null);
    setShowSupplierModal(false);
    setShowQuickSupplierModal(true);
  };

  const handleSaveSupplier = async () => {
    const fromQuickModal = showQuickSupplierModal;

    if (!newSupplier.company_name.trim()) {
      setSupplierError('Company name is required.');
      return;
    }
    if (!newSupplier.location.trim()) {
      setSupplierError('Location is required.');
      return;
    }
    if (!newSupplier.primary_email.trim()) {
      setSupplierError('Primary email is required.');
      return;
    }
    const emailTrim = newSupplier.primary_email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setSupplierError('Please enter a valid primary email address.');
      return;
    }

    const rawContacts = Array.isArray(newSupplier.contacts) ? newSupplier.contacts : [];
    const hasNamedContact = rawContacts.some((c) => (c.name || '').trim());
    if (!hasNamedContact) {
      setSupplierError('At least one contact name is required.');
      return;
    }

    setSupplierError(null);
    let contacts = rawContacts
      .map((c) => ({
        name: (c.name || '').trim(),
        role: (c.role || '').trim() || null,
        email: (c.email || '').trim() || null,
        phone: (c.phone || '').trim() || null,
        whatsapp: (c.whatsapp || '').trim() || null,
        is_primary: !!c.is_primary,
      }))
      .filter((c) => c.name || c.email || c.phone || c.whatsapp);

    if (contacts.length > 0) {
      if (!contacts.some((c) => c.is_primary)) {
        contacts[0] = { ...contacts[0], is_primary: true };
      } else {
        let seenPrimary = false;
        contacts = contacts.map((c) => {
          if (c.is_primary && !seenPrimary) {
            seenPrimary = true;
            return c;
          }
          return { ...c, is_primary: false };
        });
      }
    }

    let notesVal = newSupplier.notes.trim() || null;
    if (fromQuickModal) {
      const tag = 'Added from procurement request.';
      notesVal = notesVal ? `${notesVal}\n\n${tag}` : tag;
    }

    const payload = {
      supplier_code: editingSupplier?.supplier_code || null,
      company_name: newSupplier.company_name.trim(),
      industry: newSupplier.industry.trim() || null,
      category_products: newSupplier.category_products.trim() || null,
      location: newSupplier.location.trim() || null,
      primary_email: emailTrim,
      primary_phone: newSupplier.primary_phone.trim() || null,
      website: newSupplier.website.trim() || null,
      notes: notesVal,
      bank_details: newSupplier.bank_details.trim() || null,
      contacts,
      ...(editingSupplier ? {} : { is_active: true }),
    };

    try {
      setSupplierSaving(true);
      await runWakeUp();
      if (editingSupplier) {
        const { error } = await procurementSupabase
          .from('suppliers')
          .update(payload)
          .eq('id', editingSupplier.id);
        if (error) throw error;
      } else {
        const { data: insertedRows, error } = await procurementSupabase
          .from('suppliers')
          .insert(payload)
          .select('id');
        if (error) throw error;
        const newId = insertedRows?.[0]?.id;
        if (fromQuickModal && newId) {
          setNewRequest((prev) => ({
            ...prev,
            invitedSupplierIds: Array.from(
              new Set([...(Array.isArray(prev.invitedSupplierIds) ? prev.invitedSupplierIds : []), newId]),
            ),
          }));
        }
      }
      setShowSupplierModal(false);
      setShowQuickSupplierModal(false);
      setEditingSupplier(null);
      setNewSupplier(emptySupplierForm());
      await fetchSuppliers();
    } catch (e) {
      console.warn('[Procurement] saveSupplier', e);
      setSupplierError(e?.message || 'Failed to save supplier.');
    } finally {
      setSupplierSaving(false);
    }
  };

  const handleConfirmDeleteSupplier = async () => {
    if (!supplierToDelete) return;
    try {
      await runWakeUp();
      const { error } = await procurementSupabase
        .from('suppliers')
        .delete()
        .eq('id', supplierToDelete.id);
      if (error) throw error;
      setSupplierToDelete(null);
      await fetchSuppliers();
    } catch (e) {
      console.warn('[Procurement] deleteSupplier', e);
      setSupplierError(e?.message || 'Failed to delete supplier.');
      setSupplierToDelete(null);
    }
  };

  const filteredRfqRecords = useMemo(() => {
    const q = rfqSearch.trim().toLowerCase();
    if (!q) return rfqRecords;
    return rfqRecords.filter(
      (r) =>
        (r.refNo || '').toLowerCase().includes(q) ||
        (r.client || '').toLowerCase().includes(q) ||
        (r.subject || '').toLowerCase().includes(q) ||
        (r.tenderNo || '').toLowerCase().includes(q),
    );
  }, [rfqRecords, rfqSearch]);

  const handleOpenNewRfq = () => {
    setEditingRfqId(null);
    setRfqModalError(null);
    setRfqForm(createEmptyRfqForm());
    setShowRfqModal(true);
  };

  const handleOpenEditRfq = (rec) => {
    setEditingRfqId(rec.id);
    setRfqModalError(null);
    setRfqForm({
      refNo: rec.refNo || '',
      documentDate: rec.documentDate || '',
      client: rec.client || '',
      clientCountry: rec.clientCountry || '',
      subject: rec.subject || '',
      tenderNo: rec.tenderNo || '',
      warranty: rec.warranty || '',
      delivery: rec.delivery || '',
      status: rec.status || 'Draft',
      items:
        rec.items && rec.items.length
          ? rec.items.map((it) => ({ ...it }))
          : [{ id: Date.now(), item: '', description: '', qty: '', unitPrice: '' }],
    });
    setShowRfqModal(true);
  };

  const handleRfqItemChange = (itemId, key, value) => {
    setRfqForm((prev) => ({
      ...prev,
      items: prev.items.map((row) => (row.id === itemId ? { ...row, [key]: value } : row)),
    }));
  };

  const handleAddRfqItemRow = () => {
    setRfqForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { id: Date.now(), item: '', description: '', qty: '', unitPrice: '' },
      ],
    }));
  };

  const handleRemoveRfqItemRow = (itemId) => {
    setRfqForm((prev) => {
      if (prev.items.length <= 1) return prev;
      return { ...prev, items: prev.items.filter((row) => row.id !== itemId) };
    });
  };

  const handleSaveRfq = async () => {
    if (!rfqForm.refNo.trim()) {
      setRfqModalError('Reference number (Ref. No.) is required.');
      return;
    }
    if (!rfqForm.client.trim()) {
      setRfqModalError('Client / recipient is required.');
      return;
    }
    const normalizedItems = rfqForm.items.map((row, idx) => ({
      id: row.id ?? idx + 1,
      item: (row.item || '').trim(),
      description: (row.description || '').trim(),
      qty: Number(row.qty) || 0,
      unitPrice: Math.max(0, Number(row.unitPrice) || 0),
    }));
    const payloadItems = normalizedItems.filter(
      (it) => it.item || it.description || it.qty || it.unitPrice,
    );
    if (payloadItems.length === 0) {
      setRfqModalError('Add at least one line with item details, quantity, and unit price (OMR).');
      return;
    }
    setRfqModalError(null);
    const sub = payloadItems.reduce((sum, it) => sum + rfqLineTotal(it.qty, it.unitPrice), 0);
    const { taxAmount, grandTotal } = computePoTaxAndGrandTotal(sub, 0);
    const status = rfqForm.status || 'Draft';
    const headerPayload = {
      ref_no: rfqForm.refNo.trim(),
      document_date: rfqForm.documentDate || null,
      client: rfqForm.client.trim(),
      client_country: (rfqForm.clientCountry || '').trim() || null,
      subject: (rfqForm.subject || '').trim() || null,
      tender_no: (rfqForm.tenderNo || '').trim() || null,
      warranty: (rfqForm.warranty || '').trim() || null,
      delivery: (rfqForm.delivery || '').trim() || null,
      status,
      tax_percent: PO_TAX_PERCENT_FIXED,
      subtotal_amount: sub,
      tax_amount: taxAmount,
      grand_total: grandTotal,
    };
    const buildLineRows = (rfqId) =>
      payloadItems.map((it, idx) => ({
        rfq_id: rfqId,
        line_no: idx + 1,
        item_name: it.item,
        description: it.description || null,
        quantity: it.qty,
        unit_price: it.unitPrice,
      }));

    setRfqSaving(true);
    try {
      await runWakeUp();
      if (editingRfqId) {
        const { error: updErr } = await procurementSupabase
          .from('rfqs')
          .update(headerPayload)
          .eq('id', editingRfqId);
        if (updErr) throw updErr;
        const { error: delErr } = await procurementSupabase
          .from('rfq_line_items')
          .delete()
          .eq('rfq_id', editingRfqId);
        if (delErr) throw delErr;
        const { error: insErr } = await procurementSupabase
          .from('rfq_line_items')
          .insert(buildLineRows(editingRfqId));
        if (insErr) throw insErr;
      } else {
        const { data: inserted, error: insHeaderErr } = await procurementSupabase
          .from('rfqs')
          .insert(headerPayload)
          .select('id')
          .single();
        if (insHeaderErr) throw insHeaderErr;
        const newId = inserted?.id;
        if (!newId) throw new Error('RFQ was created but no id was returned.');
        const { error: insLinesErr } = await procurementSupabase
          .from('rfq_line_items')
          .insert(buildLineRows(newId));
        if (insLinesErr) throw insLinesErr;
      }
      await fetchRfqs();
      setShowRfqModal(false);
      setEditingRfqId(null);
    } catch (e) {
      console.warn('[Procurement] saveRfq', e);
      setRfqModalError(e?.message || 'Failed to save RFQ.');
    } finally {
      setRfqSaving(false);
    }
  };

  const handleConfirmDeleteRfq = async () => {
    if (!rfqToDelete) return;
    setRfqDeleteError(null);
    try {
      await runWakeUp();
      const { error } = await procurementSupabase.from('rfqs').delete().eq('id', rfqToDelete.id);
      if (error) throw error;
      setRfqToDelete(null);
      await fetchRfqs();
    } catch (e) {
      console.warn('[Procurement] deleteRfq', e);
      setRfqDeleteError(e?.message || 'Failed to delete RFQ.');
    }
  };

  const handleDownloadRfqPdf = async (rec) => {
    if (!rec) return;
    setRfqPdfError(null);
    const today = new Date().toISOString().slice(0, 10);
    const items = Array.isArray(rec.items) ? rec.items : [];
    const tableRows = items.length
      ? items.map((row, idx) => {
          const qty = Number(row.qty) || 0;
          const unitPrice = Math.max(0, Number(row.unitPrice) || 0);
          const lineTotal = rfqLineTotal(qty, unitPrice);
          const item = (row.item || '').trim();
          const desc = (row.description || '').trim();
          const combinedDesc = desc ? `${item}${item ? ' — ' : ''}${desc}` : item || '—';
          return [
            String(idx + 1),
            combinedDesc,
            '',
            qty || '',
            unitPrice ? unitPrice.toFixed(2) : '',
            lineTotal ? lineTotal.toFixed(2) : '',
          ];
        })
      : [['1', '—', '', '—', '—', '—']];
    const subtotal = rfqSubtotal(items);
    const { taxAmount, grandTotal } = computePoTaxAndGrandTotal(subtotal, 0);
    const storedGrand =
      rec.grandTotal != null && !Number.isNaN(Number(rec.grandTotal)) ? Number(rec.grandTotal) : null;
    const totalForWords = storedGrand != null ? storedGrand : grandTotal;
    const amountWords = totalForWords ? amountToWordsAED(totalForWords) : '';
    const refNo = (rec.refNo || 'Draft').replace(/</g, '_');
    const warranty = (rec.warranty || '').trim() || '____________________________________________';
    const delivery = (rec.delivery || '').trim() || '____________________________________________';
    const subject = (rec.subject || '').trim() || '____________________________________________';

    try {
      const headerPromise = loadImageDataUrl(headImgUrl);
      let stampDataUrl = null;
      let signatureDataUrl = null;
      if (!rfqPdfOmitStampSignature) {
        const { stampUrl, signatureUrl } = await getDefaultStampAndSignature();
        [stampDataUrl, signatureDataUrl] = await Promise.all([
          stampUrl ? loadImageDataUrl(stampUrl) : Promise.resolve(null),
          signatureUrl ? loadImageDataUrl(signatureUrl) : Promise.resolve(null),
        ]);
      }
      const headerDataUrl = await headerPromise;

      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = PDF_MARGIN_MM;

      if (headerDataUrl) {
        pdf.addImage(headerDataUrl, 'PNG', 0, 0, pageW, PDF_HEADER_HEIGHT_MM);
      }

      let y = PDF_HEADER_HEIGHT_MM + 8;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('REQUEST FOR QUOTATION', pageW / 2, y, { align: 'center' });
      y += 10;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const metaW = (pageW - margin * 2 - 8) / 2;
      const metaLeft = margin;
      const metaRight = margin + metaW + 8;
      const yMetaStart = y;
      const leftRows = [
        ['Reference No.', rec.refNo || '—'],
        ['Client', rec.client || '—'],
        ['Subject', rec.subject || '—'],
        ['Tender No.', rec.tenderNo || '—'],
      ];
      const rightRows = [
        ['Date', rec.documentDate || today],
        ['Country', rec.clientCountry || '—'],
        ['Status', rec.status || '—'],
      ];
      leftRows.forEach(([label, val], i) => {
        const yy = yMetaStart + i * 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, metaLeft, yy, { maxWidth: metaW - 4 });
        pdf.setFont('helvetica', 'normal');
        pdf.text(String(val ?? '—'), metaLeft + 42, yy, { maxWidth: metaW - 46 });
      });
      rightRows.forEach(([label, val], i) => {
        const yy = yMetaStart + i * 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, metaRight, yy, { maxWidth: metaW - 4 });
        pdf.setFont('helvetica', 'normal');
        pdf.text(String(val ?? '—'), metaRight + 38, yy, { maxWidth: metaW - 42 });
      });
      y = yMetaStart + 22;

      autoTable(pdf, {
        head: [['SR NO', 'Item description', 'Units', 'QTY', 'Unit price', 'Total price']],
        body: tableRows.map((row) => row.map(String)),
        startY: y,
        margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
        tableWidth: pageW - margin * 2,
        showHead: 'everyPage',
        headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 12 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 18 },
          3: { cellWidth: 14 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
        },
        didDrawPage: () => {
          drawPdfHeaderFooter(pdf, pageW, pageH, headerDataUrl);
        },
      });

      y = pdf.lastAutoTable?.finalY ?? y;
      y += 8;

      autoTable(pdf, {
        body: [
          ['SUB TOTAL (OMR)', (subtotal ?? 0).toFixed(2)],
          ['SHIPPING COST', '0.00'],
          ['TAX (5% of subtotal)', (taxAmount ?? 0).toFixed(2)],
          ['TOTAL AMOUNT TO PAY (AED)', (storedGrand ?? grandTotal ?? 0).toFixed(2)],
          [amountWords || 'Amount in words: _______________________________', ''],
        ],
        startY: y,
        margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
        tableWidth: pageW - margin * 2,
        theme: 'plain',
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
      });
      y = pdf.lastAutoTable?.finalY ?? y;
      y += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Warranty: ${warranty}`, margin, y);
      y += 6;
      pdf.text(`Delivery terms: ${delivery}`, margin, y);
      y += 5;
      pdf.text(`Notes: ${subject}`, margin, y, { maxWidth: pageW - margin * 2 });
      y += 14;

      const boxGap = 4;
      const boxW = (pageW - margin * 2 - boxGap) / 2;
      const boxH = 32;
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.3);
      pdf.rect(margin, y, boxW, boxH, 'S');
      pdf.rect(margin + boxW + boxGap, y, boxW, boxH, 'S');
      pdf.setFontSize(8);
      pdf.setTextColor(107, 114, 128);
      pdf.text('Authorized Signature', margin + boxW / 2, y + 5, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      if (!rfqPdfOmitStampSignature && signatureDataUrl) {
        try {
          pdf.addImage(
            signatureDataUrl,
            'PNG',
            margin + 4,
            y + 8,
            Math.min(boxW - 8, 35),
            Math.min(boxH - 12, 16),
          );
        } catch {
          // ignore
        }
      }
      const stampBoxLeft = margin + boxW + boxGap;
      const stampImgW = Math.min(boxW - 8, 35);
      const stampImgH = Math.min(boxH - 12, 16);
      const stampImgY = y + 8;
      if (!rfqPdfOmitStampSignature && stampDataUrl) {
        try {
          pdf.addImage(
            stampDataUrl,
            'PNG',
            stampBoxLeft + (boxW - stampImgW) / 2,
            stampImgY,
            stampImgW,
            stampImgH,
          );
        } catch {
          // ignore
        }
      }
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(107, 114, 128);
      pdf.text('Company Stamp', stampBoxLeft + boxW / 2, stampImgY + stampImgH / 2 + 1.2, {
        align: 'center',
      });
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      pdf.save(`rfq-${refNo}.pdf`);
    } catch (e) {
      console.warn('[Procurement] downloadRfqPdf', e);
      setRfqPdfError(e?.message || 'Failed to generate PDF.');
    }
  };

  // ---------- Render helpers ----------

  const renderRfqTab = () => (
    <>
      <div className="tenders-page-header">
        <h2 className="tenders-page-title">Request for Quotation (RFQ)</h2>
        <div className="tenders-header-actions">
          <button type="button" className="tenders-header-btn primary" onClick={handleOpenNewRfq}>
            <IconPlus />
            New RFQ
          </button>
        </div>
      </div>

      <div className="tenders-filter-row">
        <div className="tenders-filter-left">
          {rfqPdfError && (
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: 'var(--danger, #b91c1c)' }}>
              {rfqPdfError}
            </p>
          )}
          <div className="tenders-form-toggle-wrap" style={{ margin: '0 0 0.5rem' }}>
            <input
              type="checkbox"
              id="rfqPdfOmitStampSignature"
              checked={rfqPdfOmitStampSignature}
              onChange={(e) => setRfqPdfOmitStampSignature(e.target.checked)}
            />
            <label htmlFor="rfqPdfOmitStampSignature" style={{ fontSize: '0.85rem' }}>
              No stamp / signature on PDF
            </label>
          </div>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Header fields and line items follow the ECDT quotation layout (Ref. No., client, subject,
            tender no., OMR line pricing). Totals use fixed {PO_TAX_PERCENT_FIXED}% VAT on subtotal and
            are stored in Supabase for each RFQ. PDF uses the same layout as purchase orders (logo,
            table, totals block, signatures and stamp).
          </p>
        </div>
        <div className="tenders-filter-right">
          <button type="button" className="tenders-utility-btn" disabled title="Available after backend">
            Excel
          </button>
          <div className="tenders-search">
            <input
              type="text"
              placeholder="Search"
              aria-label="Search RFQs"
              value={rfqSearch}
              onChange={(e) => setRfqSearch(e.target.value)}
            />
            <span className="tenders-search-icon">
              <IconSearch />
            </span>
          </div>
        </div>
      </div>

      <div className="tenders-table-wrapper">
        <table className="tenders-table">
          <thead>
            <tr>
              <th>Ref. No.</th>
              <th>Date</th>
              <th>Client</th>
              <th>Subject</th>
              <th>Tender No.</th>
              <th>Lines</th>
              <th>Total inc. VAT (OMR)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rfqLoading ? (
              <tr>
                <td colSpan={9} className="tenders-empty-cell">
                  Loading RFQs…
                </td>
              </tr>
            ) : rfqError ? (
              <tr>
                <td colSpan={9} className="tenders-empty-cell">
                  {rfqError}
                </td>
              </tr>
            ) : filteredRfqRecords.length === 0 ? (
              <tr>
                <td colSpan={9} className="tenders-empty-cell">
                  {rfqRecords.length === 0
                    ? 'No RFQs yet. Use "New RFQ" to add one.'
                    : 'No RFQs match your search.'}
                </td>
              </tr>
            ) : (
              filteredRfqRecords.map((row) => {
                const displayTotal =
                  row.grandTotal != null && !Number.isNaN(Number(row.grandTotal))
                    ? Number(row.grandTotal)
                    : rfqTotalsWithVat(row.items).grandTotal;
                return (
                  <tr key={row.id}>
                    <td>{row.refNo}</td>
                    <td>{row.documentDate || '—'}</td>
                    <td>
                      <span style={{ display: 'block' }}>{row.client}</span>
                      <span
                        style={{
                          display: 'block',
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {row.clientCountry || '—'}
                      </span>
                    </td>
                    <td>{row.subject || '—'}</td>
                    <td>{row.tenderNo || '—'}</td>
                    <td>{row.items?.length ?? 0}</td>
                    <td>
                      {displayTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td>
                      <span className={`tenders-badge ${getRfqStatusBadge(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className="tenders-row-actions">
                        <button
                          type="button"
                          className="tenders-row-icon-btn"
                          aria-label="Download PDF"
                          title="Download RFQ PDF"
                          onClick={() => {
                            setRfqPdfError(null);
                            handleDownloadRfqPdf(row);
                          }}
                        >
                          <IconDownload />
                        </button>
                        <button
                          type="button"
                          className="tenders-row-icon-btn"
                          aria-label="Edit"
                          title="Edit RFQ"
                          onClick={() => handleOpenEditRfq(row)}
                        >
                          <IconEdit />
                        </button>
                        <button
                          type="button"
                          className="tenders-row-icon-btn"
                          aria-label="Delete"
                          title="Delete RFQ"
                          onClick={() => {
                            setRfqDeleteError(null);
                            setRfqToDelete(row);
                          }}
                        >
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderRequestsTab = () => (
    <>
      <div className="tenders-page-header">
        <h2 className="tenders-page-title">Procurement Requests</h2>
        <div className="tenders-header-actions">
          <button
            type="button"
            className="tenders-header-btn primary"
            onClick={handleOpenNewRequest}
          >
            <IconPlus />
            Add Request
          </button>
        </div>
      </div>

      <div className="tenders-filter-row">
        <div className="tenders-filter-left">
          <select
            className="tenders-filter-select"
            value={requestDeptFilter}
            onChange={(e) => {
              setRequestDeptFilter(e.target.value);
            }}
          >
            <option value="">Department / Project</option>
            {requestDepartments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            className="tenders-filter-select"
            value={requestStatusFilter}
            onChange={(e) => {
              setRequestStatusFilter(e.target.value);
            }}
          >
            <option value="">Status</option>
            {[
              'Draft',
              'RFQ Sent',
              'Offers Received',
              'Supplier Selected',
              'Converted to Order',
              'Closed',
            ].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            className="tenders-filter-select"
            value={requestByFilter}
            onChange={(e) => {
              setRequestByFilter(e.target.value);
            }}
          >
            <option value="">Requested by</option>
            {requestPeople.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            className="tenders-filter-select"
            value={requestSupplierFilter}
            onChange={(e) => {
              setRequestSupplierFilter(e.target.value);
            }}
          >
            <option value="">Supplier</option>
            {requestSuppliers.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <div className="tenders-date-filter">
            <label>
              Date from
              <input
                type="date"
                value={requestFrom}
                onChange={(e) => setRequestFrom(e.target.value)}
              />
            </label>
          </div>
          <div className="tenders-date-filter">
            <label>
              to
              <input
                type="date"
                value={requestTo}
                onChange={(e) => setRequestTo(e.target.value)}
              />
            </label>
          </div>
          <div className="tenders-form-toggle-wrap" style={{ alignSelf: 'center', marginLeft: '0.25rem' }}>
            <input
              type="checkbox"
              id="prPdfOmitStampSignature"
              checked={prPdfOmitStampSignature}
              onChange={(e) => setPrPdfOmitStampSignature(e.target.checked)}
            />
            <label htmlFor="prPdfOmitStampSignature" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              No stamp / signature on PDF
            </label>
          </div>
        </div>
        <div className="tenders-filter-right">
          <button type="button" className="tenders-utility-btn">
            Excel
          </button>
          <div className="tenders-search">
            <input
              type="text"
              placeholder="Search"
              aria-label="Search requests"
              value={requestSearch}
              onChange={(e) => setRequestSearch(e.target.value)}
            />
            <span className="tenders-search-icon">
              <IconSearch />
            </span>
          </div>
        </div>
      </div>

      {requestError && (
        <p
          className="tenders-empty-cell"
          style={{ marginBottom: '0.5rem', color: 'var(--danger, #b91c1c)', fontSize: '0.9rem' }}
        >
          {requestError}
        </p>
      )}
      <div className="tenders-table-wrapper">
        <table className="tenders-table">
          <thead>
            <tr>
              <th>Request ID</th>
              <th>Title</th>
              <th>Department / Project</th>
              <th>Requested by</th>
              <th>Needed date</th>
              <th>Suppliers invited</th>
              <th>Selected supplier</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requestsLoading ? (
              <tr>
                <td colSpan={9} className="tenders-empty-cell">
                  Loading requests…
                </td>
              </tr>
            ) : filteredRequests.length === 0 ? (
              <tr>
                <td colSpan={9} className="tenders-empty-cell">
                  No requests found.
                </td>
              </tr>
            ) : (
              filteredRequests.map((row) => (
                <tr key={row.id}>
                  <td>{row.requestId}</td>
                  <td>
                    <button
                      type="button"
                      className="tenders-table-link-btn"
                      onClick={() => handleOpenRequestDetails(row)}
                    >
                      {row.title}
                    </button>
                  </td>
                  <td>{row.departmentProject}</td>
                  <td>{row.requestedBy}</td>
                  <td>{row.neededDate || '—'}</td>
                  <td>{row.suppliersInvited || 0}</td>
                  <td>{row.selectedSupplier || '—'}</td>
                  <td>
                    <span
                      className={`tenders-badge ${getRequestStatusBadge(row.status)}`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td>
                    <div className="tenders-row-actions">
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="View"
                        title="View details"
                        onClick={() => handleOpenRequestDetails(row)}
                      >
                        <IconEye />
                      </button>
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="Edit"
                        title="Edit (UI only)"
                        onClick={() => handleOpenRequestDetails(row)}
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="Delete"
                        title="Delete request"
                        onClick={() => setRequestToDelete(row)}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderWorkflowStepper = (current) => {
    return (
      <div className="workflow-indicator" style={{ marginBottom: '1rem' }}>
        <div className="workflow-steps">
          {PROCUREMENT_STAGES.map((stage, index) => {
            const currentIndex = PROCUREMENT_STAGES.indexOf(current);
            const isPast = index < currentIndex;
            const isActive = index === currentIndex;
            return (
              <React.Fragment key={stage}>
                <div
                  className={`workflow-step${
                    isActive ? ' active' : isPast ? ' past' : ''
                  }`}
                >
                  <span className="workflow-step-dot" />
                  <span className="workflow-step-label">{stage}</span>
                </div>
                {index < PROCUREMENT_STAGES.length - 1 && (
                  <div
                    className={`workflow-connector${
                      isPast ? ' past' : ''
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const renderOrdersTab = () => (
    <>
      <div className="tenders-page-header">
        <h2 className="tenders-page-title">Purchase Orders</h2>
        <div className="tenders-header-actions">
          <button
            type="button"
            className="tenders-header-btn primary"
            onClick={handleOpenCreateOrder}
          >
            <IconPlus />
            Create Order
          </button>
        </div>
      </div>

      <div className="tenders-filter-row">
        <div className="tenders-filter-left">
          <div className="tenders-form-toggle-wrap" style={{ alignSelf: 'center', margin: 0 }}>
            <input
              type="checkbox"
              id="poPdfOmitStampSignature"
              checked={poPdfOmitStampSignature}
              onChange={(e) => setPoPdfOmitStampSignature(e.target.checked)}
            />
            <label htmlFor="poPdfOmitStampSignature" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              No stamp / signature on PDF
            </label>
          </div>
        </div>
        <div className="tenders-filter-right">
          <button type="button" className="tenders-utility-btn">
            Excel
          </button>
          <div className="tenders-search">
            <input
              type="text"
              placeholder="Search"
              aria-label="Search orders"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
            />
            <span className="tenders-search-icon">
              <IconSearch />
            </span>
          </div>
        </div>
      </div>

      {orderError && (
        <p style={{ marginBottom: '0.5rem', color: 'var(--danger, #b91c1c)', fontSize: '0.9rem' }}>
          {orderError}
        </p>
      )}
      <div className="tenders-table-wrapper">
        <table className="tenders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Supplier</th>
              <th>Linked Request</th>
              <th>Total Amount</th>
              <th>Order Date</th>
              <th>Delivery Status</th>
              <th>Payment Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ordersLoading ? (
              <tr>
                <td colSpan={8} className="tenders-empty-cell">
                  Loading orders…
                </td>
              </tr>
            ) : filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={8} className="tenders-empty-cell">
                  No orders found.
                </td>
              </tr>
            ) : (
              filteredOrders.map((row) => (
                <tr key={row.id}>
                  <td>{row.orderId}</td>
                  <td>{row.supplier}</td>
                  <td>
                    <span style={{ display: 'block' }}>{row.linkedRequestId}</span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {row.linkedRequestTitle}
                    </span>
                  </td>
                  <td>
                    {row.totalAmount?.toLocaleString()} OMR
                  </td>
                  <td>{row.orderDate}</td>
                  <td>
                    <span
                      className={`tenders-badge ${getDeliveryBadge(
                        row.deliveryStatus,
                      )}`}
                    >
                      {row.deliveryStatus}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`tenders-badge ${getPaymentBadge(
                        row.paymentStatus,
                      )}`}
                    >
                      {row.paymentStatus}
                    </span>
                  </td>
                  <td>
                    <div className="tenders-row-actions">
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="View"
                        title="View details"
                        onClick={() => handleOpenOrderDetails(row)}
                      >
                        <IconEye />
                      </button>
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="Edit"
                        title="Edit (UI only)"
                        onClick={() => handleOpenOrderDetails(row)}
                      >
                        <IconEdit />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderSuppliersTab = () => (
    <>
      <div className="tenders-page-header">
        <h2 className="tenders-page-title">Suppliers</h2>
        <div className="tenders-header-actions">
          <button
            type="button"
            className="tenders-header-btn primary"
            onClick={handleOpenAddSupplier}
          >
            <IconPlus />
            Add Supplier
          </button>
        </div>
      </div>

      <div className="tenders-filter-row">
        <div className="tenders-filter-left" />
        <div className="tenders-filter-right">
          <button type="button" className="tenders-utility-btn">
            Excel
          </button>
          <div className="tenders-search">
            <input
              type="text"
              placeholder="Search"
              aria-label="Search suppliers"
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
            />
            <span className="tenders-search-icon">
              <IconSearch />
            </span>
          </div>
        </div>
      </div>

      <div className="tenders-table-wrapper">
        <table className="tenders-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Industry</th>
              <th>Category / Products</th>
              <th>Location</th>
              <th>Primary contact</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSuppliers.length === 0 ? (
              <tr>
                <td colSpan={7} className="tenders-empty-cell">
                  No suppliers found.
                </td>
              </tr>
            ) : (
              filteredSuppliers.map((row) => (
                <tr key={row.id}>
                  <td>{row.company_name}</td>
                  <td>{row.industry || '—'}</td>
                  <td>{row.category_products || '—'}</td>
                  <td>{row.location || '—'}</td>
                  <td>{getPrimaryContact(row)?.name || '—'}</td>
                  <td>{row.primary_email || '—'}</td>
                  <td>{row.primary_phone || '—'}</td>
                  <td>
                    <div className="tenders-row-actions">
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="View"
                        title="View (UI only)"
                      >
                        <IconEye />
                      </button>
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="Edit"
                        title="Edit supplier"
                        onClick={() => {
                          const contactsArray = Array.isArray(row.contacts) && row.contacts.length
                            ? row.contacts
                            : [
                                {
                                  name: getPrimaryContact(row)?.name || '',
                                  role: getPrimaryContact(row)?.role || '',
                                  email: row.primary_email || '',
                                  phone: row.primary_phone || '',
                                  whatsapp: getPrimaryContact(row)?.whatsapp || '',
                                  is_primary: true,
                                },
                              ];
                          const contactsWithIds = contactsArray.map((c, index) => ({
                            id: `contact-${row.id}-${index}`,
                            ...c,
                          }));
                          setEditingSupplier(row);
                          setNewSupplier({
                            company_name: row.company_name || '',
                            industry: row.industry || '',
                            category_products: row.category_products || '',
                            location: row.location || '',
                            primary_email: row.primary_email || '',
                            primary_phone: row.primary_phone || '',
                            website: row.website || '',
                            notes: row.notes || '',
                            bank_details: row.bank_details || '',
                            contacts: contactsWithIds,
                          });
                          setShowQuickSupplierModal(false);
                          setShowSupplierModal(true);
                        }}
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="Delete"
                        title="Delete supplier"
                        onClick={() => setSupplierToDelete(row)}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div className="tenders-page-card">
      <div className="tenders-form-tabs" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <button
          type="button"
          className={`tenders-form-tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Requests
        </button>
        <button
          type="button"
          className={`tenders-form-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Orders
        </button>
        <button
          type="button"
          className={`tenders-form-tab ${activeTab === 'suppliers' ? 'active' : ''}`}
          onClick={() => setActiveTab('suppliers')}
        >
          Suppliers
        </button>
        <button
          type="button"
          className={`tenders-form-tab ${activeTab === 'rfq' ? 'active' : ''}`}
          onClick={() => setActiveTab('rfq')}
        >
          RFQ
        </button>
      </div>

      {activeTab === 'requests' && renderRequestsTab()}
      {activeTab === 'orders' && renderOrdersTab()}
      {activeTab === 'suppliers' && renderSuppliersTab()}
      {activeTab === 'rfq' && renderRfqTab()}

      {/* Request details drawer */}
      {showRequestDrawer && selectedRequest && (
        <div
          className="tenders-drawer"
          style={{ right: 0 }}
        >
          <div className="tenders-drawer-header">
            <h3 className="tenders-drawer-title">
              {selectedRequest.requestId}
            </h3>
            <button
              type="button"
              className="tenders-drawer-close"
              onClick={() => setShowRequestDrawer(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="tenders-drawer-body">
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="tenders-header-btn primary"
                onClick={() => handleOpenEditRequest(selectedRequest)}
              >
                <IconEdit />
                Edit
              </button>
              <button
                type="button"
                className="tenders-header-btn"
                onClick={() =>
                  handleDownloadRequestPdf(selectedRequest, selectedRequestItems.map((r) => ({
                    item: r.item,
                    quantity: r.quantity,
                    specification: r.specification,
                    estimatedCost: r.estimated_cost,
                  })))
                }
              >
                Download PDF
              </button>
              <div className="tenders-form-toggle-wrap" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  id="prPdfOmitStampSignatureDrawer"
                  checked={prPdfOmitStampSignature}
                  onChange={(e) => setPrPdfOmitStampSignature(e.target.checked)}
                />
                <label htmlFor="prPdfOmitStampSignatureDrawer" style={{ fontSize: '0.8rem' }}>
                  No stamp / signature on PDF
                </label>
              </div>
            </div>
            {renderWorkflowStepper(statusToCurrentStage(selectedRequest.status))}
            <div className="tenders-info-card">
              <div className="tenders-info-grid">
                <div className="tenders-info-item">
                  <label>Title</label>
                  <span>{selectedRequest.title}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Department / Project</label>
                  <span>{selectedRequest.departmentProject || '—'}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Requested by</label>
                  <span>{selectedRequest.requestedBy || '—'}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Needed date</label>
                  <span>{selectedRequest.neededDate || '—'}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Suppliers invited</label>
                  <span>{selectedRequest.suppliersInvited || 0}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Selected supplier</label>
                  <span>{selectedRequest.selectedSupplier || '—'}</span>
                </div>
              </div>
            </div>
            {selectedRequestItems.length > 0 && (
              <>
                <h4 style={{ fontSize: '0.9rem', marginTop: '1rem', marginBottom: '0.5rem' }}>
                  Items
                </h4>
                <div className="tenders-table-wrapper">
                  <table className="tenders-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Quantity</th>
                        <th>Specification</th>
                        <th>Est. cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRequestItems.map((row, idx) => (
                        <tr key={row.id || idx}>
                          <td>{row.line_no ?? idx + 1}</td>
                          <td>{row.item}</td>
                          <td>{row.quantity}</td>
                          <td>{row.specification || '—'}</td>
                          <td>{row.estimated_cost != null ? row.estimated_cost : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Request modal */}
      {showRequestModal && (
        <div
          className="tenders-modal-backdrop"
          onClick={() => setShowRequestModal(false)}
        >
          <div
            className="tenders-modal"
            style={{ maxWidth: '820px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">
                {editingRequestId ? 'Edit Procurement Request' : 'Add Procurement Request'}
              </h2>
              <button
                type="button"
                className="tenders-modal-close"
                onClick={() => { setShowRequestModal(false); setEditingRequestId(null); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="tenders-form-body">
              <h3 style={{ fontSize: '0.95rem', marginTop: 0 }}>A. Basic Information</h3>
              <div className="tenders-form-grid">
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Request Title</label>
                  <input
                    className="tenders-form-input"
                    value={newRequest.title}
                    onChange={(e) =>
                      setNewRequest((prev) => ({ ...prev, title: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Department</label>
                  <select
                    className="tenders-form-select"
                    value={newRequest.departmentId}
                    onChange={(e) =>
                      setNewRequest((prev) => ({
                        ...prev,
                        departmentId: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select department</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="tenders-form-label">Project</label>
                  <select
                    className="tenders-form-select"
                    value={newRequest.projectId}
                    onChange={(e) =>
                      setNewRequest((prev) => ({
                        ...prev,
                        projectId: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="tenders-form-label">Requested By</label>
                  <input
                    className="tenders-form-input"
                    value={newRequest.requestedBy}
                    onChange={(e) =>
                      setNewRequest((prev) => ({
                        ...prev,
                        requestedBy: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Needed By Date</label>
                  <input
                    type="date"
                    className="tenders-form-input"
                    value={newRequest.neededDate}
                    onChange={(e) =>
                      setNewRequest((prev) => ({
                        ...prev,
                        neededDate: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Priority</label>
                  <select
                    className="tenders-form-select"
                    value={newRequest.priority}
                    onChange={(e) =>
                      setNewRequest((prev) => ({
                        ...prev,
                        priority: e.target.value,
                      }))
                    }
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div>
                  <label className="tenders-form-label">Status</label>
                  <select
                    className="tenders-form-select"
                    value={newRequest.status}
                    onChange={(e) =>
                      setNewRequest((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="Draft">Draft</option>
                    <option value="RFQ Sent">RFQ Sent</option>
                    <option value="Offers Received">Offers Received</option>
                    <option value="Supplier Selected">Supplier Selected</option>
                    <option value="Converted to Order">Converted to Order</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Notes</label>
                  <textarea
                    className="tenders-form-textarea"
                    value={newRequest.notes}
                    onChange={(e) =>
                      setNewRequest((prev) => ({ ...prev, notes: e.target.value }))
                    }
                  />
                </div>
              </div>

              <h3 style={{ fontSize: '0.95rem', marginTop: '1.5rem' }}>B. Items Required</h3>
              <div className="tenders-table-wrapper">
                <table className="tenders-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Quantity</th>
                      <th>Specification</th>
                      <th>Estimated cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newItems.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.item}
                            onChange={(e) =>
                              handleItemChange(row.id, 'item', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.quantity}
                            onChange={(e) =>
                              handleItemChange(row.id, 'quantity', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.specification}
                            onChange={(e) =>
                              handleItemChange(row.id, 'specification', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.estimatedCost}
                            onChange={(e) =>
                              handleItemChange(
                                row.id,
                                'estimatedCost',
                                e.target.value,
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={handleAddItemRow}
                style={{ marginTop: '0.75rem' }}
              >
                + Add Item
              </button>

              <h3 style={{ fontSize: '0.95rem', marginTop: '1.5rem' }}>
                C. Invite Suppliers
              </h3>
              <div className="tenders-form-grid">
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Suppliers</label>
                  <div
                    style={{
                      border: '1px solid var(--card-border)',
                      borderRadius: 6,
                      padding: '0.5rem 0.75rem',
                      maxHeight: 180,
                      overflowY: 'auto',
                    }}
                  >
                    {suppliersLoading && (
                      <p
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                          margin: 0,
                        }}
                      >
                        Loading suppliers…
                      </p>
                    )}
                    {!suppliersLoading && suppliers.length === 0 && (
                      <p
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                          margin: 0,
                        }}
                      >
                        No suppliers found. Add one below.
                      </p>
                    )}
                    {!suppliersLoading &&
                      suppliers.map((s) => {
                        const checked = Array.isArray(newRequest.invitedSupplierIds)
                          ? newRequest.invitedSupplierIds.includes(s.id)
                          : false;
                        return (
                          <label
                            key={s.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontSize: '0.85rem',
                              padding: '0.1rem 0',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSupplierForNewRequest(s.id)}
                            />
                            <span>{s.company_name || s.companyName}</span>
                            <span
                              style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-muted)',
                              }}
                            >
                              {s.category_products ||
                                s.industry ||
                                s.category ||
                                ''}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Add supplier</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="tenders-footer-btn tenders-footer-btn-primary"
                      onClick={handleOpenQuickAddSupplier}
                    >
                      Add supplier…
                    </button>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Opens a form with full supplier details (company, location, email, contacts, etc.).
                    </span>
                  </div>
                </div>
                {!editingRequestId ? (
                  <div className="tenders-form-row tenders-form-toggle-wrap">
                    <input
                      type="checkbox"
                      id="sendRfqOnSave"
                      checked={newRequest.sendRfqOnSave}
                      onChange={(e) =>
                        setNewRequest((prev) => ({
                          ...prev,
                          sendRfqOnSave: e.target.checked,
                        }))
                      }
                    />
                    <label htmlFor="sendRfqOnSave">
                      Send RFQ email to selected suppliers when saving this request
                    </label>
                  </div>
                ) : (
                  <div className="tenders-form-row tenders-form-toggle-wrap">
                    <input
                      type="checkbox"
                      id="sendEmailOnUpdate"
                      checked={newRequest.sendEmailOnUpdate}
                      onChange={(e) =>
                        setNewRequest((prev) => ({
                          ...prev,
                          sendEmailOnUpdate: e.target.checked,
                        }))
                      }
                    />
                    <label htmlFor="sendEmailOnUpdate">
                      Send email updates to invited suppliers when saving
                    </label>
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                  <div className="tenders-form-toggle-wrap" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      id="prPdfOmitStampSignatureModal"
                      checked={prPdfOmitStampSignature}
                      onChange={(e) => setPrPdfOmitStampSignature(e.target.checked)}
                    />
                    <label htmlFor="prPdfOmitStampSignatureModal" style={{ fontSize: '0.8rem' }}>
                      No stamp / signature on PDF
                    </label>
                  </div>
                  <button
                    type="button"
                    className="tenders-footer-btn"
                    onClick={() =>
                      handleDownloadRequestPdf(
                        editingRequestId && selectedRequest
                          ? {
                              ...selectedRequest,
                              title: newRequest.title,
                              departmentProject: getDepartmentProjectLabel() || newRequest.departmentProject,
                              requestedBy: newRequest.requestedBy,
                              neededDate: newRequest.neededDate,
                              status: newRequest.status,
                            }
                          : null,
                        newItems,
                      )}
                    title="Download request as PDF"
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
            <div className="tenders-modal-footer">
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() => { setShowRequestModal(false); setEditingRequestId(null); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-primary"
                onClick={handleSaveNewRequest}
                disabled={requestSaving}
              >
                {requestSaving ? 'Saving…' : editingRequestId ? 'Update Request' : 'Save Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Request confirmation */}
      {requestToDelete && (
        <div
          className="tenders-modal-backdrop"
          onClick={() => setRequestToDelete(null)}
        >
          <div
            className="tenders-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">Delete request?</h2>
              <button
                type="button"
                className="tenders-modal-close"
                onClick={() => setRequestToDelete(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="tenders-modal-body">
              <p>
                Are you sure you want to delete &quot;
                {requestToDelete.title}&quot; (
                {requestToDelete.requestId})?
              </p>
            </div>
            <div className="tenders-modal-footer">
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-danger"
                onClick={handleConfirmDeleteRequest}
              >
                Delete
              </button>
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() => setRequestToDelete(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order details drawer */}
      {showOrderDrawer && selectedOrder && (
        <div
          className="tenders-drawer"
          style={{ right: 0 }}
        >
          <div className="tenders-drawer-header">
            <h3 className="tenders-drawer-title">
              {selectedOrder.orderId}
            </h3>
            <button
              type="button"
              className="tenders-drawer-close"
              onClick={() => setShowOrderDrawer(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="tenders-drawer-body">
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="tenders-header-btn primary"
                onClick={() => handleDownloadPoDoc(selectedOrder)}
                title="Download purchase order PDF"
              >
                Download PO
              </button>
              <div className="tenders-form-toggle-wrap" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  id="poPdfOmitStampSignatureDrawer"
                  checked={poPdfOmitStampSignature}
                  onChange={(e) => setPoPdfOmitStampSignature(e.target.checked)}
                />
                <label htmlFor="poPdfOmitStampSignatureDrawer" style={{ fontSize: '0.8rem' }}>
                  No stamp / signature on PDF
                </label>
              </div>
              {orderPdfError && (
                <span style={{ color: 'var(--danger, #b91c1c)', fontSize: '0.9rem', alignSelf: 'center' }}>{orderPdfError}</span>
              )}
            </div>
            <div className="tenders-info-card">
              <div className="tenders-info-grid">
                <div className="tenders-info-item">
                  <label>Supplier</label>
                  <span>{selectedOrder.supplier}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Contact</label>
                  <span>
                    {selectedOrder.supplierContact} ·{' '}
                    {selectedOrder.supplierPhone}
                  </span>
                </div>
                <div className="tenders-info-item">
                  <label>Email</label>
                  <span>{selectedOrder.supplierEmail}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Linked request</label>
                  <span>
                    {selectedOrder.linkedRequestId} –{' '}
                    {selectedOrder.linkedRequestTitle}
                  </span>
                </div>
                <div className="tenders-info-item">
                  <label>Order date</label>
                  <span>{selectedOrder.orderDate}</span>
                </div>
                <div className="tenders-info-item">
                  <label>Total amount</label>
                  <span>
                    {selectedOrder.totalAmount?.toLocaleString()} OMR
                  </span>
                </div>
              </div>
            </div>

            <h4 style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
              Ordered Items
            </h4>
            <div className="tenders-table-wrapper">
              <table className="tenders-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((it, idx) => (
                    <tr key={idx}>
                      <td>{it.description}</td>
                      <td>{it.quantity}</td>
                      <td>{it.unit}</td>
                      <td>{it.amount?.toLocaleString()} OMR</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
              Delivery
            </h4>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() =>
                  updateOrder(selectedOrder.id, { deliveryStatus: 'Partial' })
                }
              >
                Mark Partial Delivery
              </button>
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-primary"
                onClick={() =>
                  updateOrder(selectedOrder.id, { deliveryStatus: 'Delivered' })
                }
              >
                Mark Delivered
              </button>
            </div>

            <h4 style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
              Payment
            </h4>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-primary"
                onClick={() =>
                  updateOrder(selectedOrder.id, { paymentStatus: 'Paid' })
                }
              >
                Mark as Paid
              </button>
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() =>
                  updateOrder(selectedOrder.id, { paymentStatus: 'Unpaid' })
                }
              >
                Mark as Unpaid
              </button>
            </div>
          </div>
        </div>
      )}

      {showRfqModal && (
        <div
          className="tenders-modal-backdrop tenders-form-modal"
          onClick={() => {
            if (rfqSaving) return;
            setShowRfqModal(false);
            setRfqModalError(null);
          }}
        >
          <div
            className="tenders-modal"
            style={{ maxWidth: '960px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">{editingRfqId ? 'Edit RFQ' : 'New RFQ'}</h2>
              <button
                type="button"
                className="tenders-modal-close"
                onClick={() => {
                  if (rfqSaving) return;
                  setShowRfqModal(false);
                  setRfqModalError(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="tenders-form-body">
              {rfqModalError && (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--danger, #b91c1c)' }}>
                  {rfqModalError}
                </p>
              )}
              <h3 style={{ fontSize: '0.95rem', marginTop: 0 }}>Document header</h3>
              <div className="tenders-form-grid">
                <div>
                  <label className="tenders-form-label">Ref. No.</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. ECDT/PROC/02/15/26"
                    value={rfqForm.refNo}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, refNo: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Date</label>
                  <input
                    type="date"
                    className="tenders-form-input"
                    value={rfqForm.documentDate}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, documentDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Client / recipient</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. Ministry of Health"
                    value={rfqForm.client}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, client: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Country / address line</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. Sultanate of Oman"
                    value={rfqForm.clientCountry}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, clientCountry: e.target.value }))}
                  />
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Subject</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. Quotation For Supply Interactive Screen"
                    value={rfqForm.subject}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, subject: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Tender No.</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. 2026/443"
                    value={rfqForm.tenderNo}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, tenderNo: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Warranty</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. 3 Years"
                    value={rfqForm.warranty}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, warranty: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Delivery</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. 3 - 4 Weeks"
                    value={rfqForm.delivery}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, delivery: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Status</label>
                  <select
                    className="tenders-form-select"
                    value={rfqForm.status}
                    onChange={(e) => setRfqForm((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Accepted">Accepted</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <h3 style={{ fontSize: '0.95rem', marginTop: '1.25rem' }}>Line items (OMR)</h3>
              <div className="tenders-table-wrapper" style={{ marginTop: '0.5rem' }}>
                <table className="tenders-table">
                  <thead>
                    <tr>
                      <th style={{ width: '3rem' }}>#</th>
                      <th>Item</th>
                      <th>Description</th>
                      <th style={{ width: '5.5rem' }}>Qty</th>
                      <th style={{ width: '7rem' }}>Unit price</th>
                      <th style={{ width: '7rem' }}>Line total</th>
                      <th style={{ width: '4rem' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rfqForm.items.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <input
                            className="tenders-form-input"
                            style={{ minWidth: '120px' }}
                            value={row.item}
                            onChange={(e) => handleRfqItemChange(row.id, 'item', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.description}
                            onChange={(e) => handleRfqItemChange(row.id, 'description', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="tenders-form-input"
                            value={row.qty}
                            onChange={(e) => handleRfqItemChange(row.id, 'qty', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="tenders-form-input"
                            value={row.unitPrice}
                            onChange={(e) => handleRfqItemChange(row.id, 'unitPrice', e.target.value)}
                          />
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {rfqLineTotal(row.qty, row.unitPrice).toFixed(2)}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="tenders-row-icon-btn"
                            aria-label="Remove line"
                            title="Remove line"
                            onClick={() => handleRemoveRfqItemRow(row.id)}
                            disabled={rfqForm.items.length <= 1}
                          >
                            <IconTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="tenders-footer-btn"
                style={{ marginTop: '0.5rem' }}
                onClick={handleAddRfqItemRow}
              >
                + Add line
              </button>

              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: 'var(--surface-muted, #f9fafb)',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  maxWidth: '28rem',
                }}
              >
                {(() => {
                  const sub = rfqSubtotal(rfqForm.items);
                  const { taxAmount, grandTotal } = rfqTotalsWithVat(rfqForm.items);
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                        <span>Sub total</span>
                        <span>{sub.toFixed(2)} OMR</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                        <span>VAT ({PO_TAX_PERCENT_FIXED}%)</span>
                        <span>{taxAmount.toFixed(2)} OMR</span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontWeight: 600,
                          paddingTop: '0.35rem',
                          borderTop: '1px solid var(--border, #e5e7eb)',
                        }}
                      >
                        <span>Total price with VAT (OMR)</span>
                        <span>{grandTotal.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="tenders-form-toggle-wrap" style={{ marginTop: '0.75rem' }}>
                <input
                  type="checkbox"
                  id="rfqPdfOmitStampSignatureModal"
                  checked={rfqPdfOmitStampSignature}
                  onChange={(e) => setRfqPdfOmitStampSignature(e.target.checked)}
                />
                <label htmlFor="rfqPdfOmitStampSignatureModal" style={{ fontSize: '0.8rem' }}>
                  No stamp / signature on PDF
                </label>
              </div>
              <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                After saving, use Download PDF on the RFQ row for the same document layout as purchase
                orders (amount in words and company stamp when configured).
              </p>
            </div>
            <div className="tenders-modal-footer">
              <button
                type="button"
                className="tenders-footer-btn"
                disabled={rfqSaving}
                onClick={() => {
                  setShowRfqModal(false);
                  setRfqModalError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-primary"
                disabled={rfqSaving}
                onClick={handleSaveRfq}
              >
                {rfqSaving ? 'Saving…' : 'Save RFQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rfqToDelete && (
        <div
          className="tenders-modal-backdrop"
          onClick={() => {
            setRfqToDelete(null);
            setRfqDeleteError(null);
          }}
        >
          <div className="tenders-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">Delete RFQ?</h2>
              <button
                type="button"
                className="tenders-modal-close"
                onClick={() => {
                  setRfqToDelete(null);
                  setRfqDeleteError(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="tenders-modal-body">
              {rfqDeleteError && (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--danger, #b91c1c)' }}>
                  {rfqDeleteError}
                </p>
              )}
              <p>
                Permanently delete &quot;{rfqToDelete.refNo}&quot; and its line items from Supabase? This
                cannot be undone.
              </p>
            </div>
            <div className="tenders-modal-footer">
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() => {
                  setRfqToDelete(null);
                  setRfqDeleteError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-danger"
                onClick={handleConfirmDeleteRfq}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Order modal */}
      {showCreateOrderModal && (
        <div
          className="tenders-modal-backdrop tenders-form-modal"
          onClick={() => setShowCreateOrderModal(false)}
        >
          <div
            className="tenders-modal"
            style={{ maxWidth: '820px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">Create Purchase Order</h2>
              <button
                type="button"
                className="tenders-modal-close"
                onClick={() => setShowCreateOrderModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="tenders-form-body">
              <h3 style={{ fontSize: '0.95rem', marginTop: 0 }}>Supplier & Header</h3>
              <div className="tenders-form-grid">
                <div>
                  <label className="tenders-form-label">Supplier</label>
                  <select
                    className="tenders-form-select"
                    value={newOrder.supplierId}
                    onChange={(e) =>
                      setNewOrder((prev) => ({ ...prev, supplierId: e.target.value }))
                    }
                  >
                    <option value="">— Select supplier —</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.company_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="tenders-form-label">Linked Request</label>
                  <select
                    className="tenders-form-select"
                    value={newOrder.linkedRequestId}
                    onChange={(e) =>
                      setNewOrder((prev) => ({
                        ...prev,
                        linkedRequestId: e.target.value,
                      }))
                    }
                  >
                    <option value="">— Optional —</option>
                    {requests.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.requestId} — {r.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="tenders-form-label">Purchase order ref</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. 25/12/004"
                    value={newOrder.poRef}
                    onChange={(e) =>
                      setNewOrder((prev) => ({ ...prev, poRef: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Ref quotation</label>
                  <input
                    className="tenders-form-input"
                    placeholder="Optional"
                    value={newOrder.refQuotation}
                    onChange={(e) =>
                      setNewOrder((prev) => ({ ...prev, refQuotation: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Order Date</label>
                  <input
                    type="date"
                    className="tenders-form-input"
                    value={newOrder.orderDate}
                    onChange={(e) =>
                      setNewOrder((prev) => ({ ...prev, orderDate: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Expected Delivery Date</label>
                  <input
                    type="date"
                    className="tenders-form-input"
                    value={newOrder.deliveryDate}
                    onChange={(e) =>
                      setNewOrder((prev) => ({
                        ...prev,
                        deliveryDate: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Shipping cost (OMR)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="tenders-form-input"
                    value={newOrder.shippingCost}
                    onChange={(e) =>
                      setNewOrder((prev) => ({
                        ...prev,
                        shippingCost: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Tax (fixed 5%)</label>
                  <div
                    className="tenders-form-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: 'var(--surface-muted, #f3f4f6)',
                      cursor: 'default',
                    }}
                  >
                    {(() => {
                      const sub = calculateNewOrderTotal() || 0;
                      const ship = Math.max(0, Number(newOrder.shippingCost) || 0);
                      const { taxAmount, grandTotal } = computePoTaxAndGrandTotal(sub, ship);
                      return (
                        <span style={{ fontSize: '0.9rem' }}>
                          {sub > 0
                            ? `${taxAmount.toFixed(2)} OMR (5% of ${sub.toFixed(2)} subtotal; total with shipping ${grandTotal.toFixed(2)} OMR)`
                            : '—'}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <label className="tenders-form-label">Payment mode</label>
                  <select
                    className="tenders-form-select"
                    value={newOrder.paymentMode}
                    onChange={(e) =>
                      setNewOrder((prev) => ({
                        ...prev,
                        paymentMode: e.target.value,
                      }))
                    }
                  >
                    <option value="">— Select —</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque (PDC)</option>
                    <option value="bank">Bank</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Payment Terms</label>
                  <input
                    className="tenders-form-input"
                    placeholder="e.g. 30 days from invoice"
                    value={newOrder.paymentTerms}
                    onChange={(e) =>
                      setNewOrder((prev) => ({
                        ...prev,
                        paymentTerms: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Notes</label>
                  <textarea
                    className="tenders-form-textarea"
                    value={newOrder.notes}
                    onChange={(e) =>
                      setNewOrder((prev) => ({ ...prev, notes: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Ordered by</label>
                  <input
                    className="tenders-form-input"
                    value={newOrder.orderedBy}
                    onChange={(e) =>
                      setNewOrder((prev) => ({ ...prev, orderedBy: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Approved by</label>
                  <input
                    className="tenders-form-input"
                    value={newOrder.approvedBy}
                    onChange={(e) =>
                      setNewOrder((prev) => ({ ...prev, approvedBy: e.target.value }))
                    }
                  />
                </div>
              </div>

              <h3 style={{ fontSize: '0.95rem', marginTop: '1.5rem' }}>
                Ordered Items
              </h3>
              <div className="tenders-table-wrapper">
                <table className="tenders-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Unit Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newOrderItems.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.description}
                            onChange={(e) =>
                              handleOrderItemChange(row.id, 'description', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.qty}
                            onChange={(e) =>
                              handleOrderItemChange(row.id, 'qty', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.unit}
                            onChange={(e) =>
                              handleOrderItemChange(row.id, 'unit', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="tenders-form-input"
                            value={row.unitPrice}
                            onChange={(e) =>
                              handleOrderItemChange(row.id, 'unitPrice', e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={handleAddOrderItemRow}
                style={{ marginTop: '0.75rem' }}
              >
                + Add Item
              </button>
              <div style={{ marginTop: '0.75rem', fontSize: '0.9rem' }}>
                <strong>Order total (subtotal + shipping + 5% tax on subtotal):</strong>{' '}
                {(() => {
                  const sub = calculateNewOrderTotal() || 0;
                  if (!sub) return '—';
                  const ship = Math.max(0, Number(newOrder.shippingCost) || 0);
                  const { grandTotal } = computePoTaxAndGrandTotal(sub, ship);
                  return `${grandTotal.toFixed(2)} OMR`;
                })()}
              </div>

              <h3 style={{ fontSize: '0.95rem', marginTop: '1.5rem' }}>
                Document
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                <div className="tenders-form-toggle-wrap" style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    id="poPdfOmitStampSignatureModal"
                    checked={poPdfOmitStampSignature}
                    onChange={(e) => setPoPdfOmitStampSignature(e.target.checked)}
                  />
                  <label htmlFor="poPdfOmitStampSignatureModal" style={{ fontSize: '0.8rem' }}>
                    No stamp / signature on PDF
                  </label>
                </div>
                <button
                  type="button"
                  className="tenders-footer-btn"
                  onClick={handleDownloadPoDoc}
                  title="Download purchase order PDF (letterhead and layout as shown)"
                >
                  Download PO document
                </button>
              </div>
              {orderPdfError && (
                <p style={{ marginTop: '0.5rem', color: 'var(--danger, #b91c1c)', fontSize: '0.9rem' }}>{orderPdfError}</p>
              )}
              <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  id="sendPoEmailToSupplier"
                  type="checkbox"
                  checked={newOrder.sendPoEmailToSupplier}
                  onChange={(e) =>
                    setNewOrder((prev) => ({ ...prev, sendPoEmailToSupplier: e.target.checked }))
                  }
                />
                <label htmlFor="sendPoEmailToSupplier" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                  Email purchase order to supplier (uses primary email on file)
                </label>
              </div>
            </div>
            <div className="tenders-modal-footer">
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() => setShowCreateOrderModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-primary"
                disabled={orderSaving || !newOrder.supplierId}
                onClick={async () => {
                  const subtotal = calculateNewOrderTotal();
                  const shippingVal = Math.max(0, Number(newOrder.shippingCost) || 0);
                  const { grandTotal } = computePoTaxAndGrandTotal(subtotal, shippingVal);
                  const year = new Date().getFullYear();
                  const existingNumbers = orders
                    .filter((o) => o.orderId && o.orderId.startsWith(`PO-${year}-`))
                    .map((o) => {
                      const m = (o.orderId || '').match(/PO-\d{4}-(\d+)/);
                      return m ? parseInt(m[1], 10) : 0;
                    });
                  const maxNo = Math.max(0, ...existingNumbers);
                  const orderNo = `PO-${year}-${String(maxNo + 1).padStart(3, '0')}`;
                  setOrderError(null);
                  setOrderSaving(true);
                  try {
                    await runWakeUp();
                    const { data: inserted, error: orderErr } = await procurementSupabase
                      .from('purchase_orders')
                      .insert({
                        order_no: orderNo,
                        supplier_id: newOrder.supplierId,
                        procurement_request_id: newOrder.linkedRequestId || null,
                        po_ref: newOrder.poRef?.trim() || null,
                        ref_quotation: newOrder.refQuotation?.trim() || null,
                        order_date: newOrder.orderDate || null,
                        delivery_date: newOrder.deliveryDate || null,
                        shipping_cost: shippingVal,
                        tax_percent: PO_TAX_PERCENT_FIXED,
                        payment_mode: newOrder.paymentMode || null,
                        payment_terms: newOrder.paymentTerms?.trim() || null,
                        notes: newOrder.notes?.trim() || null,
                        ordered_by: newOrder.orderedBy?.trim() || null,
                        approved_by: newOrder.approvedBy?.trim() || null,
                        total_amount: grandTotal,
                      })
                      .select('id')
                      .single();
                    if (orderErr) throw orderErr;
                    const validItems = newOrderItems.filter(
                      (row) => (row.description || '').trim() && (Number(row.qty) || 0) > 0,
                    );
                    if (validItems.length > 0) {
                      const rows = validItems.map((row, idx) => ({
                        order_id: inserted.id,
                        line_no: idx + 1,
                        description: (row.description || '').trim(),
                        quantity: Math.max(0, Number(row.qty) || 0),
                        unit: (row.unit || '').trim() || null,
                        unit_price: Math.max(0, Number(row.unitPrice) || 0),
                      }));
                      const { error: itemsErr } = await procurementSupabase
                        .from('purchase_order_items')
                        .insert(rows);
                      if (itemsErr) throw itemsErr;
                    }
                    if (newOrder.sendPoEmailToSupplier) {
                      const supplierRecord = suppliers.find((s) => s.id === newOrder.supplierId);
                      const attachment = await buildPurchaseOrderPdfAttachment({
                        orderNo,
                        supplierName: supplierRecord?.company_name || supplierRecord?.companyName || '',
                        orderDate: newOrder.orderDate || '',
                        deliveryDate: newOrder.deliveryDate || '',
                        paymentMode: newOrder.paymentMode || '',
                        paymentTerms: newOrder.paymentTerms?.trim() || '',
                        notes: newOrder.notes?.trim() || '',
                        lineItems: newOrderItems,
                        totalAmount: grandTotal,
                      });
                      const poRes = await sendPurchaseOrderEmailToSupplier({
                        supplierRecord,
                        orderNo,
                        orderDate: newOrder.orderDate || '',
                        deliveryDate: newOrder.deliveryDate || '',
                        paymentMode: newOrder.paymentMode || '',
                        paymentTerms: newOrder.paymentTerms?.trim() || '',
                        notes: newOrder.notes?.trim() || '',
                        lineItems: newOrderItems,
                        totalAmount: grandTotal,
                        orderedBy: newOrder.orderedBy?.trim() || '',
                        approvedBy: newOrder.approvedBy?.trim() || '',
                        poRef: newOrder.poRef?.trim() || '',
                        refQuotation: newOrder.refQuotation?.trim() || '',
                        attachment,
                      });
                      if (!poRes.success) {
                        setOrderError(
                          `Order ${orderNo} saved. Email to supplier failed: ${poRes.error || 'unknown'}.`,
                        );
                      }
                    }
                    // Internal notification: purchase order created
                    try {
                      const supplierRecord = suppliers.find((s) => s.id === newOrder.supplierId);
                      const internalRes = await sendPurchaseOrderCreatedEmail({
                        orderNo,
                        supplierName: supplierRecord?.company_name || supplierRecord?.companyName || '',
                        orderDate: newOrder.orderDate || '',
                        deliveryDate: newOrder.deliveryDate || '',
                        totalAmount: grandTotal,
                        paymentMode: newOrder.paymentMode || '',
                        paymentTerms: newOrder.paymentTerms?.trim() || '',
                        orderedBy: newOrder.orderedBy?.trim() || '',
                        approvedBy: newOrder.approvedBy?.trim() || '',
                      });
                      if (!internalRes.success && !internalRes.skipped) {
                        console.warn('[Procurement] order created email failed:', internalRes.error);
                      }
                    } catch (emailErr) {
                      console.warn('[Procurement] order created email exception:', emailErr);
                    }
                    await fetchOrders();
                    setShowCreateOrderModal(false);
                  } catch (e) {
                    console.warn('[Procurement] saveOrder', e);
                    setOrderError(e?.message || 'Failed to save order.');
                  } finally {
                    setOrderSaving(false);
                  }
                }}
              >
                {orderSaving ? 'Saving…' : 'Save Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / edit Supplier modal (Suppliers tab or procurement request quick add) */}
      {(showSupplierModal || showQuickSupplierModal) && (
        <div
          className="tenders-modal-backdrop"
          onClick={() => {
            setShowSupplierModal(false);
            setShowQuickSupplierModal(false);
          }}
        >
          <div
            className="tenders-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">
                {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
              </h2>
              <button
                type="button"
                className="tenders-modal-close"
                onClick={() => {
                  setShowSupplierModal(false);
                  setShowQuickSupplierModal(false);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="tenders-form-body">
              {supplierError && (
                <p
                  style={{
                    margin: '0 0 0.75rem',
                    fontSize: '0.85rem',
                    color: 'var(--danger, #b91c1c)',
                  }}
                >
                  {supplierError}
                </p>
              )}
              <div className="tenders-form-grid">
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Company name *</label>
                  <input
                    className="tenders-form-input"
                    required
                    value={newSupplier.company_name}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        company_name: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Industry</label>
                  <input
                    className="tenders-form-input"
                    value={newSupplier.industry}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        industry: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Category / Products</label>
                  <input
                    className="tenders-form-input"
                    value={newSupplier.category_products}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        category_products: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Location *</label>
                  <input
                    className="tenders-form-input"
                    required
                    value={newSupplier.location}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        location: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Primary email *</label>
                  <input
                    className="tenders-form-input"
                    type="email"
                    required
                    autoComplete="email"
                    value={newSupplier.primary_email}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        primary_email: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Primary phone</label>
                  <input
                    className="tenders-form-input"
                    value={newSupplier.primary_phone}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        primary_phone: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="tenders-form-label">Website</label>
                  <input
                    className="tenders-form-input"
                    value={newSupplier.website}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        website: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Notes</label>
                  <textarea
                    className="tenders-form-textarea"
                    value={newSupplier.notes}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Bank details</label>
                  <textarea
                    className="tenders-form-textarea"
                    placeholder="Bank name, account number, IBAN, Swift, etc."
                    value={newSupplier.bank_details}
                    onChange={(e) =>
                      setNewSupplier((prev) => ({
                        ...prev,
                        bank_details: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="tenders-form-row">
                  <label className="tenders-form-label">Contacts *</label>
                  <div>
                    {(newSupplier.contacts || []).map((c, index) => (
                      <div
                        key={c.id || index}
                        className="tenders-form-grid"
                        style={{ marginBottom: '0.5rem', alignItems: 'flex-start' }}
                      >
                        <input
                          className="tenders-form-input"
                          placeholder="Name *"
                          value={c.name || ''}
                          onChange={(e) =>
                            setNewSupplier((prev) => ({
                              ...prev,
                              contacts: prev.contacts.map((row, i) =>
                                i === index ? { ...row, name: e.target.value } : row,
                              ),
                            }))
                          }
                        />
                        <input
                          className="tenders-form-input"
                          placeholder="Role (Sales, Procurement…)"
                          value={c.role || ''}
                          onChange={(e) =>
                            setNewSupplier((prev) => ({
                              ...prev,
                              contacts: prev.contacts.map((row, i) =>
                                i === index ? { ...row, role: e.target.value } : row,
                              ),
                            }))
                          }
                        />
                        <input
                          className="tenders-form-input"
                          placeholder="Email (optional)"
                          value={c.email || ''}
                          onChange={(e) =>
                            setNewSupplier((prev) => ({
                              ...prev,
                              contacts: prev.contacts.map((row, i) =>
                                i === index ? { ...row, email: e.target.value } : row,
                              ),
                            }))
                          }
                        />
                        <input
                          className="tenders-form-input"
                          placeholder="Phone (optional)"
                          value={c.phone || ''}
                          onChange={(e) =>
                            setNewSupplier((prev) => ({
                              ...prev,
                              contacts: prev.contacts.map((row, i) =>
                                i === index ? { ...row, phone: e.target.value } : row,
                              ),
                            }))
                          }
                        />
                        <input
                          className="tenders-form-input"
                          placeholder="WhatsApp (optional)"
                          value={c.whatsapp || ''}
                          onChange={(e) =>
                            setNewSupplier((prev) => ({
                              ...prev,
                              contacts: prev.contacts.map((row, i) =>
                                i === index ? { ...row, whatsapp: e.target.value } : row,
                              ),
                            }))
                          }
                        />
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.8rem',
                            paddingTop: '0.3rem',
                          }}
                        >
                          <input
                            type="radio"
                            name="primaryContact"
                            checked={!!c.is_primary}
                            onChange={() =>
                              setNewSupplier((prev) => ({
                                ...prev,
                                contacts: prev.contacts.map((row, i) => ({
                                  ...row,
                                  is_primary: i === index,
                                })),
                              }))
                            }
                          />
                          Primary
                        </label>
                        {newSupplier.contacts && newSupplier.contacts.length > 1 && (
                          <button
                            type="button"
                            className="tenders-footer-btn"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={() =>
                              setNewSupplier((prev) => ({
                                ...prev,
                                contacts: prev.contacts.filter((_, i) => i !== index),
                              }))
                            }
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="tenders-footer-btn"
                      onClick={() =>
                        setNewSupplier((prev) => ({
                          ...prev,
                          contacts: [
                            ...(prev.contacts || []),
                            {
                              id: `contact-${Date.now()}`,
                              name: '',
                              role: '',
                              email: '',
                              phone: '',
                              whatsapp: '',
                              is_primary: !prev.contacts || prev.contacts.length === 0,
                            },
                          ],
                        }))
                      }
                      style={{ marginTop: '0.5rem' }}
                    >
                      + Add contact
                    </button>
                    <p
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--text-muted)',
                        marginTop: '0.25rem',
                      }}
                    >
                      At least one contact must have a name. You can add multiple contacts with
                      separate phone and WhatsApp numbers. One of them will be used as the primary
                      contact in documents.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="tenders-modal-footer">
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() => {
                  setShowSupplierModal(false);
                  setShowQuickSupplierModal(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-primary"
                onClick={handleSaveSupplier}
                disabled={supplierSaving}
              >
                {supplierSaving ? 'Saving…' : 'Save Supplier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Supplier confirmation */}
      {supplierToDelete && (
        <div
          className="tenders-modal-backdrop"
          onClick={() => setSupplierToDelete(null)}
        >
          <div
            className="tenders-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">Delete supplier?</h2>
              <button
                type="button"
                className="tenders-modal-close"
                onClick={() => setSupplierToDelete(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="tenders-modal-body">
              <p>
                Are you sure you want to delete &quot;
                {supplierToDelete.company_name || supplierToDelete.companyName}&quot;?
              </p>
            </div>
            <div className="tenders-modal-footer">
              <button
                type="button"
                className="tenders-footer-btn tenders-footer-btn-danger"
                onClick={handleConfirmDeleteSupplier}
              >
                Delete
              </button>
              <button
                type="button"
                className="tenders-footer-btn"
                onClick={() => setSupplierToDelete(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

